<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="uw-body">

        <!-- Top Ribbon -->
        <header class="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <div class="tw-flex tw-items-center tw-gap-2">
            <h2 class="tw-flex tw-items-center tw-gap-2 tw-text-xl tw-font-bold tw-text-[#1E4276]" style="white-space:nowrap;">
              <v-icon class="tw-cursor-pointer tw-mr-2" color="#1E4276" @click="$router.push({ name: 'AgentProductSelect' })">mdi-arrow-left</v-icon>
              New Motor Quote
            </h2>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <button
              v-if="hasLiveQuote"
              class="uw-new-btn"
              @click="showResetConfirm = true"
            >
              <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-refresh</v-icon>
              New Quote
            </button>
          </div>
          <div class="agent-profile">
            <div class="tw-w-10 tw-h-10 tw-rounded-full tw-bg-[#00AEEF] tw-text-white tw-flex tw-items-center tw-justify-center tw-font-bold">JD</div>
          </div>
        </header>

        <div class="quote-container">
          <div class="quote-layout">

            <!-- LEFT: Form Column -->
            <div class="quote-form-column">

              <!-- Section 1: Cover Type & Usage -->
              <div class="section-card">
                <div class="section-header">
                  <span class="section-number">1</span>
                  <h3>Cover Type &amp; Usage</h3>
                </div>

                <div class="paired-section">
                  <!-- Cover Type -->
                  <div class="sub-section">
                    <label class="sub-label">Cover Type</label>
                    <div class="radio-pill-group" style="flex-direction: column;" :style="yomForcesTPO ? 'pointer-events:none;opacity:0.6;' : ''">
                      <label class="radio-pill" :style="yomForcesTPO && form.cover_type !== 'comprehensive' ? 'opacity:0.4' : ''">
                        <input type="radio" v-model="form.cover_type" name="cover_type" value="comprehensive" :disabled="yomForcesTPO">
                        <div class="radio-pill-content">
                          <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="48" height="48" rx="12" fill="#EBF8FF" />
                            <path d="M24 8L12 13V22.5C12 30.2 17.1 37.3 24 39.5C30.9 37.3 36 30.2 36 22.5V13L24 8Z" fill="#00AEEF" />
                            <path d="M20 22L23 25L28.5 18" stroke="#FFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>Comprehensive</span>
                        </div>
                      </label>
                      <label class="radio-pill">
                        <input type="radio" v-model="form.cover_type" name="cover_type" value="tpo" :disabled="yomForcesTPO">
                        <div class="radio-pill-content">
                          <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="48" height="48" rx="12" fill="#FFF5F5" />
                            <path d="M24 16V26M24 30H24.01" stroke="#E53E3E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>Third Party Only</span>
                        </div>
                      </label>

                    </div>
                  </div>

                  <!-- Usage -->
                  <div class="sub-section">
                    <label class="sub-label">Usage</label>
                    <div class="radio-pill-group" style="flex-direction: column;">
                      <label class="radio-pill">
                        <input type="radio" v-model="form.usage_type" name="usage_type" value="private">
                        <div class="radio-pill-content">
                          <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="48" height="48" rx="12" fill="#EAF0F6" />
                            <path d="M12 26V32H16V30H32V32H36V26H12Z" fill="#1E4276" />
                            <path d="M14 26L16 16H32L34 26M14 26H34" stroke="#1E4276" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>Private</span>
                        </div>
                      </label>
                      <label class="radio-pill" style="opacity: 0.5; cursor: not-allowed; pointer-events: none;">
                        <input type="radio" v-model="form.usage_type" name="usage_type" value="commercial" disabled>
                        <div class="radio-pill-content">
                          <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="48" height="48" rx="12" fill="#FFFBE6" />
                            <path d="M28 28H10V14H28V28ZM28 28H36V22L32 18H28V28Z" stroke="#D6A800" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          <span>Commercial</span>
                        </div>
                      </label>
                    </div>

                    <div v-show="form.usage_type === 'commercial'" style="margin-top: 8px;">
                      <v-select
                        v-model="form.commercial_usage"
                        :items="['Taxi / PSV', 'Delivery Van', 'General Cartage', 'Motorcycle Courier']"
                        placeholder="Select type..."
                        outlined
                        dense
                        hide-details
                        class="custom-input"
                      ></v-select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Section 2: Vehicle Details -->
              <div class="section-card">
                <div class="section-header">
                  <span class="section-number">2</span>
                  <h3>Vehicle Details</h3>
                </div>

                <div class="form-row" style="margin-bottom: 12px;">
                  <div class="input-group">
                    <label class="input-label">Make</label>
                    <v-autocomplete
                      v-model="form.make"
                      :items="carMakes"
                      item-text="makeDetails"
                      item-value="makeDetails"
                      placeholder="Select Make"
                      outlined flat dense hide-details
                      class="custom-input"
                    ></v-autocomplete>
                  </div>
                  <div class="input-group">
                    <label class="input-label">Model</label>
                    <v-autocomplete
                      v-model="form.model"
                      :items="carModels"
                      item-text="modelname"
                      item-value="modelname"
                      placeholder="Select Model"
                      outlined flat dense hide-details
                      class="custom-input"
                    ></v-autocomplete>
                  </div>
                </div>

                <div class="form-row">
                  <div class="input-group">
                    <label class="input-label">Year of Manufacture</label>
                    <v-autocomplete
                      v-model="form.yom"
                      :items="availableYears"
                      placeholder="Select Year"
                      outlined flat dense hide-details
                      class="custom-input"
                    ></v-autocomplete>
                    <div v-if="yomForcesTPO" class="tw-mt-2" style="display:flex;align-items:center;gap:5px;background:#EBF8FF;border:1px solid #BAE6FD;border-radius:6px;padding:6px 10px;font-size:0.72rem;font-weight:600;color:#0369a1;">
                      <v-icon x-small color="#0369a1">mdi-lock-outline</v-icon>
                      Vehicle is older than 12 years — cover automatically set to TPO.
                    </div>
                  </div>
                  <div class="input-group" v-if="form.cover_type !== 'tpo'">
                    <label class="input-label">Estimated Value (Ksh)</label>
                    <v-text-field
                      v-model="formattedValueInput"
                      type="text"
                      placeholder="e.g. 1,500,000"
                      autocomplete="off"
                      outlined flat dense hide-details
                      class="custom-input"
                      maxlength="10"
                    ></v-text-field>
                    <div class="tw-text-[0.7rem] tw-text-gray-400 tw-mt-1.5">Min: Ksh 500,000</div>
                  </div>
                </div>
              </div>

              <!-- ═══════════════════════════════════════════════════
                   Section 3: Additional Benefits (Comprehensive)
              ════════════════════════════════════════════════════ -->
              <div class="section-card" v-if="form.cover_type === 'comprehensive'">
                <div class="section-header">
                  <span class="section-number">3</span>
                  <h3>Additional Benefits</h3>
                </div>

                <p class="tw-text-sm tw-text-gray-500 tw-mb-4">
                  Windscreen and infotainment come free with comprehensive cover. Tick any paid extras the client wants.
                </p>

                <!-- Loading State -->
                <div v-if="benefitsLoading" class="tw-flex tw-items-center tw-gap-3 tw-py-6 tw-justify-center">
                  <v-progress-circular indeterminate color="#00AEEF" size="22" width="2"></v-progress-circular>
                  <span class="tw-text-sm tw-text-gray-400">Loading available benefits…</span>
                </div>

                <!-- Updated: Using shared BenefitLimitSelector component -->
                <benefit-limit-selector
                  :benefits="fetchedChargeableBenefits"
                  :selected-addons.sync="form.addons_dynamic"
                  :custom-limits.sync="form.benefit_custom_limits"
                  :loading="benefitsLoading"
                  :updating="benefitUpdating"
                  :premium-lookup="benefitPremiumMap"
                  :car-value="form.value"
                  :basic-premium="basicPremium"
                  @change="handleBenefitToggle"
                />

                <div v-if="fetchedChargeableBenefits.length === 0 && !benefitsLoading" class="tw-py-6 tw-text-center tw-text-gray-400 tw-text-sm">
                  No additional benefits available for this cover.
                </div>

                <div v-else class="tw-py-6 tw-text-center tw-text-gray-400 tw-text-sm">
                  No additional benefits available for this cover.
                </div>

                <!-- Free Benefits Accordion -->
                <div class="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-mt-4">
                  <button
                    class="tw-w-full tw-flex tw-justify-between tw-items-center tw-bg-gray-50 tw-px-4 tw-py-3 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-100 tw-transition-colors"
                    @click.prevent="freeBenefitsExpanded = !freeBenefitsExpanded"
                  >
                    <span class="tw-flex tw-items-center tw-gap-2">
                      <v-icon x-small color="#00AEEF">mdi-gift-outline</v-icon>
                      Benefits Included in Your Cover
                    </span>
                    <v-icon color="gray" small>{{ freeBenefitsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </button>
                  <div v-show="freeBenefitsExpanded" class="tw-bg-white tw-p-4 tw-text-sm tw-border-t tw-border-gray-200">
                    <benefits />
                  </div>
                </div>

                <p class="tw-flex tw-items-center tw-gap-1.5 tw-text-gray-400 tw-text-xs tw-cursor-pointer hover:tw-text-[#00AEEF] tw-transition-colors tw-mt-3">
                  <v-icon x-small color="inherit">mdi-information-outline</v-icon>
                  What does Comprehensive Insurance cover?
                </p>
              </div>

              <!-- ═══════════════════════════════════════════════════
                   Section 3: Third Party Only Benefits
              ════════════════════════════════════════════════════ -->
              <div class="section-card" v-if="form.cover_type === 'tpo'">
                <div class="section-header">
                  <span class="section-number" style="background:#1E4276;">3</span>
                  <h3>What's Covered</h3>
                </div>

                <p class="tw-text-sm tw-text-gray-500 tw-mb-4">
                  Third Party Only includes the following statutory benefits at no extra cost:
                </p>

                <div class="tpo-benefit-list">
                  <div class="tpo-benefit-row">
                    <div class="tpo-icon-wrap" style="background:#EBF8FF;">
                      <v-icon color="#00AEEF" size="20">mdi-account-group-outline</v-icon>
                    </div>
                    <div class="tpo-row-body">
                      <span class="tpo-row-title">Third Party Person Liability</span>
                      <span class="tpo-row-sub">Any One Person</span>
                    </div>
                    <div class="tpo-row-amount">
                      <span class="tpo-limit-badge">KSH 3,000,000</span>
                    </div>
                  </div>

                  <div class="tpo-benefit-row">
                    <div class="tpo-icon-wrap" style="background:#F0FFF4;">
                      <v-icon color="#38A169" size="20">mdi-seat-passenger</v-icon>
                    </div>
                    <div class="tpo-row-body">
                      <span class="tpo-row-title">Passengers Liability</span>
                      <span class="tpo-row-sub">Any One Person</span>
                    </div>
                    <div class="tpo-row-amount">
                      <span class="tpo-limit-badge tpo-badge-green">KSH 4,000,000</span>
                    </div>
                  </div>

                  <div class="tpo-benefit-row tpo-sub-row">
                    <div class="tpo-icon-wrap" style="background:transparent; border:none; box-shadow:none;"></div>
                    <div class="tpo-row-body">
                      <span class="tpo-row-sub">Any One Event</span>
                    </div>
                    <div class="tpo-row-amount">
                      <span class="tpo-limit-badge tpo-badge-green">KSH 20,000,000</span>
                    </div>
                  </div>

                  <div class="tpo-benefit-row">
                    <div class="tpo-icon-wrap" style="background:#FFF5F5;">
                      <v-icon color="#E53E3E" size="20">mdi-car-off</v-icon>
                    </div>
                    <div class="tpo-row-body">
                      <span class="tpo-row-title">Third Party Property Damage</span>
                      <span class="tpo-row-sub">Per event</span>
                    </div>
                    <div class="tpo-row-amount">
                      <span class="tpo-limit-badge tpo-badge-red">KSH 5,000,000</span>
                    </div>
                  </div>
                </div>

                <div class="tpo-flat-note">
                  <v-icon x-small color="#D6A800">mdi-information-outline</v-icon>
                  TPO is charged at a flat rate — no vehicle value required.
                </div>
              </div>

            </div>

            <!-- RIGHT: Live Quote Sidebar (Desktop) -->
            <div class="quote-sidebar">
              <div class="quote-live-card">
                <div class="card-title-label"><v-icon small color="rgba(255,255,255,0.55)" class="tw-mr-1">mdi-calculator</v-icon> Live Premium Estimate</div>

                <!-- Placeholder -->
                <div v-if="!hasQuoteData" class="quote-placeholder">
                  <v-icon large color="rgba(255,255,255,0.4)">mdi-car-outline</v-icon>
                  <p>Fill in vehicle details to see your live premium</p>
                </div>

                <!-- Live data (updates when value / vehicle fields change) -->
                <div v-else-if="quoteResponse && hasQuoteData">
                  <p class="vehicle-name">{{ liveVehicleName }}</p>
                  <p class="vehicle-value-label">{{ liveCoverLabel }} · Ksh {{ formattedValue }}</p>
                  <p v-if="liveRateLabel" class="vehicle-rate-label">{{ liveRateLabel }}</p>

                  <div v-if="liveRecalcLoading" class="live-recalc-hint">
                    <v-progress-circular indeterminate size="14" width="2" color="#7dd3fc" class="tw-mr-2" />
                    Updating premium…
                  </div>

                  <div class="live-breakdown" :class="{ 'live-breakdown--dimmed': liveRecalcLoading }">
                    <div class="live-row">
                      <span>{{ form.cover_type === 'tpo' ? 'TPO Flat Rate' : 'Basic Premium' }}</span>
                      <span>Ksh {{ formatCurrency(basicPremium) }}</span>
                    </div>
                    <template v-if="form.addons_dynamic.length > 0">
                      <div class="live-row" v-for="b in fetchedChargeableBenefits.filter(x => form.addons_dynamic.includes(x.sectCode))" :key="b.sectCode">
                        <span class="tw-capitalize">{{ b.sectDesc ? b.sectDesc.toLowerCase() : 'Benefit' }}</span>
                        <span>Ksh {{ formatCurrency(getBenefitPremium(b)) }}</span>
                      </div>
                    </template>
                    <div class="live-row dim">
                      <span>Training Levy</span>
                      <span>Ksh {{ formatCurrency(manualLevy) }}</span>
                    </div>
                    <div class="live-row dim">
                      <span>PHF</span>
                      <span>Ksh {{ formatCurrency(manualPHF) }}</span>
                    </div>
                    <div class="live-row dim">
                      <span>Stamp Duty</span>
                      <span>Ksh 40</span>
                    </div>
                    <div class="live-divider"></div>
                    <div class="live-row total">
                      <span>Estimated Total</span>
                      <span>Ksh {{ formatCurrency(manualTotalPremium) }}</span>
                    </div>
                  </div>

                  <!-- Action buttons -->
                  <div class="sidebar-actions d-print-none">
                    <button class="sidebar-action-btn" @click="emailDialogOpen = true"><v-icon x-small color="white" class="tw-mr-1">mdi-email-outline</v-icon> Email</button>
                    <button class="sidebar-action-btn" disabled style="opacity: 0.5; cursor: not-allowed;"><v-icon x-small color="white" class="tw-mr-1">mdi-whatsapp</v-icon> WhatsApp <span style="font-size:0.6rem;">(Soon)</span></button>
                    <button class="sidebar-action-btn" @click="pdfDialogOpen = true"><v-icon x-small color="white" class="tw-mr-1">mdi-printer-outline</v-icon> PDF</button>
                    <button class="sidebar-action-btn" @click="saveQuote"><v-icon x-small color="white" class="tw-mr-1">mdi-content-save-outline</v-icon> Save</button>
                  </div>
                </div>

                <!-- CTA -->
                <div v-if="hasLiveQuote" class="cta-bar">
                  <button class="btn-convert-sale" :disabled="liveRecalcLoading" @click="proceedToSale">
                    Convert to Sale
                    <v-icon small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      <!-- MOBILE: Sticky Bottom Bar -->
      <div class="mobile-bottom-bar">
        <div class="mobile-bar-summary" @click="mobileDrawerOpen = !mobileDrawerOpen">
          <div class="bar-left">
            <span class="bar-label">Quote Status</span>
            <span class="bar-total">{{ hasLiveQuote ? 'Ksh ' + formatCurrency(totalPremium) : (hasQuoteData ? 'Calculating…' : 'Enter vehicle details') }}</span>
          </div>
          <div class="bar-actions">
            <button class="expand-btn" @click.stop="mobileDrawerOpen = !mobileDrawerOpen" v-if="hasLiveQuote">
              <v-icon x-small color="white">{{ mobileDrawerOpen ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon> Details
            </button>
            <button
              v-if="hasLiveQuote"
              class="bar-cta"
              :disabled="liveRecalcLoading"
              @click.stop="proceedToSale"
              style="background:#00AEEF; color:white;"
            >
              Sale <v-icon x-small color="white">mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>
        <div :class="['mobile-bar-drawer', mobileDrawerOpen ? 'open' : '']" v-if="hasLiveQuote">
          <template v-if="quoteResponse">
            <div class="live-row">
              <span>Basic Premium</span>
              <span>Ksh {{ formatCurrency(basicPremium) }}</span>
            </div>
            <template v-if="benefitDetails.length > 0">
              <div class="live-row" v-for="b in benefitDetails" :key="b.sectionCode">
                <span class="tw-capitalize">{{ b.sectDesc ? b.sectDesc.toLowerCase() : b.sectionName }}</span>
                <span>Ksh {{ formatCurrency(b.benefitPremium) }}</span>
              </div>
            </template>
            <div class="live-row dim">
              <span>Levy + PHF + Stamp</span>
              <span>Ksh {{ formatCurrency(levy + phf + 40) }}</span>
            </div>
            <div class="live-row total">
              <span>Total</span>
              <span>Ksh {{ formatCurrency(totalPremium) }}</span>
            </div>
          </template>
          <div v-else class="tw-text-center tw-py-3 tw-opacity-50 tw-text-sm">Fill in details to see breakdown</div>

          <div class="drawer-actions">
            <button class="drawer-action-btn" @click="proceedToSale">
              <v-icon x-small color="white" class="tw-mr-1">mdi-arrow-right</v-icon>
              Convert to Sale
            </button>
          </div>
          <div class="drawer-actions d-print-none">
            <button class="drawer-action-btn" @click="emailDialogOpen = true"><v-icon x-small color="white">mdi-email-outline</v-icon> Email</button>
            <button class="drawer-action-btn" disabled style="opacity: 0.5; cursor: not-allowed;"><v-icon x-small color="white">mdi-whatsapp</v-icon> WhatsApp <span style="font-size:0.6rem;">(Soon)</span></button>
            <button class="drawer-action-btn" @click="pdfDialogOpen = true"><v-icon x-small color="white">mdi-printer-outline</v-icon> PDF</button>
            <button class="drawer-action-btn" @click="saveQuote"><v-icon x-small color="white">mdi-content-save-outline</v-icon> Save</button>
          </div>
        </div>
      </div>

      <!-- Email Dispatch Dialog -->
      <v-dialog v-model="emailDialogOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
        <div class="eq-dialog">

          <!-- Header -->
          <div class="eq-header">
            <div class="eq-header-icon">
              <v-icon size="24" color="white">mdi-email-send-outline</v-icon>
            </div>
            <div>
              <p class="eq-title">Email Quote</p>
              <p class="eq-subtitle">Send this quote directly to your customer</p>
            </div>
            <button class="eq-close" @click="emailDialogOpen = false">
              <v-icon size="18" color="white">mdi-close</v-icon>
            </button>
          </div>

          <!-- Body -->
          <div class="eq-body">
            <div class="eq-field">
              <label class="eq-label">Customer Name</label>
              <div class="eq-input-wrap">
                <v-icon size="16" color="#94a3b8" class="eq-input-icon">mdi-account-outline</v-icon>
                <input
                  v-model="customerName"
                  class="eq-input"
                  placeholder="e.g. John Kamau"
                  autocomplete="off"
                  type="text"
                />
              </div>
            </div>

            <div class="eq-field">
              <label class="eq-label">Customer Email <span class="eq-req">*</span></label>
              <div class="eq-input-wrap">
                <v-icon size="16" color="#94a3b8" class="eq-input-icon">mdi-email-outline</v-icon>
                <input
                  v-model="customerEmail"
                  class="eq-input"
                  placeholder="customer@email.com"
                  autocomplete="off"
                  type="email"
                />
              </div>
            </div>

            <!-- What they'll receive -->
            <div class="eq-preview-strip">
              <v-icon x-small color="#00AEEF" class="tw-mr-1.5">mdi-information-outline</v-icon>
              The customer will receive a quote email with full premium breakdown.
            </div>
          </div>

          <!-- Footer -->
          <div class="eq-footer">
            <button class="eq-btn-cancel" @click="emailDialogOpen = false">Cancel</button>
            <button
              class="eq-btn-send"
              :disabled="!customerEmail || sendingEmail"
              @click="sendEmailQuote"
            >
              <v-progress-circular v-if="sendingEmail" indeterminate size="14" width="2" color="#1E4276" class="tw-mr-2"></v-progress-circular>
              <v-icon v-else x-small color="#1E4276" class="tw-mr-1.5">mdi-send</v-icon>
              {{ sendingEmail ? 'Sending…' : 'Send Quote' }}
            </button>
          </div>

        </div>
      </v-dialog>

      <!-- PDF Download Dialog -->
      <v-dialog v-model="pdfDialogOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
        <div class="eq-dialog">
          <div class="eq-header">
            <div class="eq-header-icon">
              <v-icon size="24" color="white">mdi-file-pdf-box</v-icon>
            </div>
            <div>
              <p class="eq-title">Download PDF Quote</p>
              <p class="eq-subtitle">Optionally add customer details to the document</p>
            </div>
            <button class="eq-close" @click="pdfDialogOpen = false">
              <v-icon size="18" color="white">mdi-close</v-icon>
            </button>
          </div>

          <div class="eq-body">
            <div class="eq-field">
              <label class="eq-label">Customer Name <span class="tw-text-gray-400 tw-font-normal">(optional)</span></label>
              <div class="eq-input-wrap">
                <v-icon size="16" color="#94a3b8" class="eq-input-icon">mdi-account-outline</v-icon>
                <input
                  v-model="pdfClientName"
                  class="eq-input"
                  placeholder="e.g. John Kamau"
                  autocomplete="off"
                  type="text"
                />
              </div>
            </div>
            <div class="eq-field">
              <label class="eq-label">Customer Email <span class="tw-text-gray-400 tw-font-normal">(optional)</span></label>
              <div class="eq-input-wrap">
                <v-icon size="16" color="#94a3b8" class="eq-input-icon">mdi-email-outline</v-icon>
                <input
                  v-model="pdfClientEmail"
                  class="eq-input"
                  placeholder="customer@email.com"
                  autocomplete="off"
                  type="email"
                />
              </div>
            </div>
            <div class="eq-preview-strip">
              <v-icon x-small color="#00AEEF" class="tw-mr-1.5">mdi-information-outline</v-icon>
              A PDF quote with full premium breakdown will open in a new tab.
            </div>
          </div>

          <div class="eq-footer">
            <button class="eq-btn-cancel" @click="pdfDialogOpen = false">Cancel</button>
            <button
              class="eq-btn-send"
              :disabled="generatingPdf"
              @click="confirmPdfDownload"
            >
              <v-progress-circular v-if="generatingPdf" indeterminate size="14" width="2" color="#1E4276" class="tw-mr-2"></v-progress-circular>
              <v-icon v-else x-small color="#1E4276" class="tw-mr-1.5">mdi-download</v-icon>
              {{ generatingPdf ? 'Generating…' : 'Open PDF' }}
            </button>
          </div>
        </div>
      </v-dialog>

      <div class="print-container">
        <!-- Letterhead -->
        <div class="pq-letterhead">
          <div class="pq-logo-wrap">
            <img src="/pacis-logo-tagline.png" alt="Pacis Insurance" class="pq-logo" />
          </div>
          <div class="pq-letterhead-right">
            <p class="pq-insurer-name">Pacis Insurance</p>
            <p class="pq-insurer-addr">Pacis Centre, 4th Floor, off Waiyaki Wy</p>
            <p class="pq-insurer-addr">P.O BOX 1870-00200, Nairobi, Kenya</p>
            <p class="pq-insurer-addr">E-mail: info@paciskenya.com</p>
          </div>
        </div>

        <!-- Title band -->
        <div class="pq-title-band">
          <span class="pq-doc-type">MOTOR INSURANCE INVOICE</span>
          <span class="pq-doc-date">Date: {{ new Date().toLocaleDateString('en-KE', { day:'2-digit', month:'long', year:'numeric' }) }}</span>
        </div>

        <!-- Two-column info grid -->
        <div class="pq-info-grid">
          <!-- Left: Client -->
          <div class="pq-info-block">
            <p class="pq-info-heading">BILL TO</p>
            <table class="pq-info-table">
              <tr><td class="pq-info-key">Name</td><td class="pq-info-val">{{ form.fullName || 'N/A' }}</td></tr>
              <tr><td class="pq-info-key">Email</td><td class="pq-info-val">{{ form.email || 'N/A' }}</td></tr>
            </table>
          </div>
          <!-- Right: Vehicle -->
          <div class="pq-info-block">
            <p class="pq-info-heading">VEHICLE DETAILS</p>
            <table class="pq-info-table">
              <tr><td class="pq-info-key">Make / Model</td><td class="pq-info-val">{{ form.make }} {{ form.model }}</td></tr>
              <tr><td class="pq-info-key">Year</td><td class="pq-info-val">{{ form.yom }}</td></tr>
              <tr><td class="pq-info-key">Usage</td><td class="pq-info-val" style="text-transform:capitalize;">{{ form.usage_type }}</td></tr>
              <tr v-if="form.cover_type !== 'tpo'"><td class="pq-info-key">Insured Value</td><td class="pq-info-val">Ksh {{ formattedValue }}</td></tr>
              <tr><td class="pq-info-key">Cover Type</td><td class="pq-info-val">{{ COVER_TYPE_LABELS[form.cover_type] || 'N/A' }}</td></tr>
            </table>
          </div>
        </div>

        <!-- Premium table -->
        <p class="pq-section-heading">INVOICE SUMMARY</p>
        <table class="pq-premium-table">
          <thead>
            <tr>
              <th>Item Description</th>
              <th class="pq-col-amt">Amount (Ksh)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ form.cover_type === 'tpo' ? 'TPO Flat Rate — Motor Insurance' : 'Motor Insurance Premium' }}</td>
              <td class="pq-col-amt">{{ formatCurrency(basicPremium) }}</td>
            </tr>
            <tr v-for="b in benefitDetails" :key="b.sectionCode">
              <template v-if="(b.benefitPremium || 0) > 0">
                <td style="text-transform:capitalize;">{{ b.sectDesc ? b.sectDesc.toLowerCase() : b.sectionName }}</td>
                <td class="pq-col-amt">{{ formatCurrency(b.benefitPremium) }}</td>
              </template>
            </tr>
            <tr class="pq-subtotal-row">
              <td>Training Levy (0.2%)</td>
              <td class="pq-col-amt">{{ formatCurrency(manualLevy) }}</td>
            </tr>
            <tr class="pq-subtotal-row">
              <td>PHF (0.25%)</td>
              <td class="pq-col-amt">{{ formatCurrency(manualPHF) }}</td>
            </tr>
            <tr class="pq-subtotal-row">
              <td>Stamp Duty</td>
              <td class="pq-col-amt">40</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="pq-total-row">
              <td>GRAND TOTAL</td>
              <td class="pq-col-amt">Ksh {{ formatCurrency(totalPremium) }}</td>
            </tr>
          </tfoot>
        </table>

        <!-- Validity note -->
        <div class="pq-validity-strip">
          <v-icon x-small color="#1E4276">mdi-clock-outline</v-icon>
          Quote is only valid for 30 days. Cover commences only upon receipt of full premium. This is a computer-generated invoice.
        </div>

        <!-- Footer -->
        <div class="pq-footer">
          <div class="pq-footer-left">
            <p class="pq-footer-line">Pacis Insurance Company Limited is regulated by the Insurance Regulatory Authority of Kenya.</p>
            <p class="pq-footer-line">This is a computer-generated document and does not require a signature.</p>
          </div>
          <div class="pq-footer-right">
            <p class="pq-footer-line tw-font-bold">Quote — {{ new Date().toLocaleDateString('en-KE') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Reset Confirm Dialog — place before closing </agent-default> -->
    <v-dialog v-model="showResetConfirm" max-width="360">
      <div class="new-sale-dialog">
        <div class="nsd-header">
          <div class="nsd-icon">
            <v-icon size="22" color="white">mdi-refresh</v-icon>
          </div>
          <div>
            <p class="nsd-title">Start a New Quote?</p>
            <p class="nsd-sub">Current quote data will be cleared.</p>
          </div>
        </div>
        <div class="nsd-body">
          <p class="nsd-desc">The generated quote will remain saved in the system. This only clears your local view so you can start a fresh quote.</p>
        </div>
        <div class="nsd-footer">
          <button class="nsd-btn-cancel" @click="showResetConfirm = false">Cancel</button>
          <button class="nsd-btn-confirm" @click="resetAndStartFresh">
            <v-icon x-small color="white" class="tw-mr-1">mdi-refresh</v-icon>
            Start Fresh
          </button>
        </div>
      </div>
    </v-dialog>
  </agent-default>
</template>

<script>
import AgentDefault from '@/components/layout/AgentDefault';
import Vue from 'vue';
import axios from 'axios';
import { mapGetters } from 'vuex';
import Benefits from '@/components/shared/Benefits';
import BenefitLimitSelector from '@/components/agent/BenefitLimitSelector';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { sanitizeAlpha, sanitizeEmail, validateEmail } from '@/utils/sanitize';
import {
  purgeLegacyKeys, savePendingQuote, loadAgentId, saveAgentId,
  clearAllSaleData,
} from '@/utils/saleCache';

const AUTH_USER_GETTER = 'auth/authenticatedUser';

const COVER_TYPE_LABELS = {
  comprehensive: 'Comprehensive',
  tpo: 'Third Party Only',
  tpf: 'TP, Fire & Theft',
};

const COVER_TYPE_IDS = {
  comprehensive: [101, 104],
  tpo: [106],
  tpf: [105],
};

const BENEFIT_FREE_LIMIT = 'Free Limit KSH 30,000';
const BENEFIT_FREE_STD_LIMIT = 'Free Standard Limit KSH 30,000';
const BEN_TP_PERSON = 'Third Party Person Liability';
const BEN_TP_PROPERTY = 'Third Party Property Damage';
const BEN_FREE_5M = 'Free limit KSH 5,000,000';
const BEN_PASSENGERS = 'Passengers Liability';

const OTHER_BENEFITS_PDF = [
  ['Legal Defence', 'Free limit KSH 10,000'],
  ['Medical Expenses', 'Free limit KSH 30,000'],
  [BEN_PASSENGERS, 'Any Person KSH 3M / Any Event KES 20M'],
  [BEN_TP_PERSON, 'Any Person KSH 3M / Any Event KSH 5M'],
  [BEN_TP_PROPERTY, BEN_FREE_5M],
];

const OTHER_BENEFITS_EMAIL = [
  { title: 'Legal Defence', limit: 'Free limit KSH 10,000' },
  { title: 'Medical Expenses', limit: 'Free limit KSH 30,000' },
  { title: BEN_PASSENGERS, limit: 'Any Person KSH 3,000,000 / Any One Event KES 20,000,000' },
  { title: BEN_TP_PERSON, limit: 'Any Person KSH 3,000,000 / Any One Event KSH 5,000,000' },
  { title: BEN_TP_PROPERTY, limit: BEN_FREE_5M },
];

export default {
  name: 'AgentUnderwriting',
  components: {
    AgentDefault,
    Benefits,
    BenefitLimitSelector,
  },
  data() {
    return {
      COVER_TYPE_LABELS,
      benefitUpdating: false,
      mobileDrawerOpen: false,
      freeBenefitsExpanded: false,
      fetchedChargeableBenefits: [],
      benefitsLoading: false,
      showResetConfirm: false,
      carMakes: [],
      carMakeObj: {},
      carModels: [],
      products: [],
      quoteResponse: null,
      benefitDetails: [],
      basePremium: 0,
      liveRecalcLoading: false,
      calcTimer: null,

      emailDialogOpen: false,
      emailDestination: 'client',
      sendingEmail: false,
      customerName: '',
      customerEmail: '',

      pdfDialogOpen: false,
      pdfClientName: '',
      pdfClientEmail: '',
      generatingPdf: false,

      form: {
        cover_type: 'comprehensive',
        usage_type: 'private',
        commercial_usage: '',
        bodyType: '',
        make: '',
        model: '',
        yom: '',
        value: '',
        addons: [],
        addons_dynamic: [],
        benefit_custom_limits: {},
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
    yomForcesTPO() {
      if (!this.form.yom) return false;
      return (new Date().getFullYear() - parseInt(this.form.yom, 10)) > 12;
    },
    availableYears() {
      const currentYear = 2026;
      const years = [];
      for (let y = currentYear; y >= 2000; y -= 1) {
        years.push(y.toString());
      }
      return years;
    },
    formattedValue() {
      return new Intl.NumberFormat().format(this.form.value || 0);
    },
    formattedValueInput: {
      get() {
        if (!this.form.value) return '';
        return new Intl.NumberFormat('en-KE').format(this.form.value);
      },
      set(val) {
        let raw = String(val).replace(/\D/g, '');
        if (raw.length > 8) raw = raw.slice(0, 8);
        const num = parseInt(raw, 10);
        this.form.value = num > 50000000 ? 50000000 : (num || '');
      },
    },
    hasQuoteData() {
      if (this.form.cover_type === 'tpo') return !!(this.form.make && this.form.model && this.form.yom);
      return parseInt(this.form.value, 10) >= 500000 && !!(this.form.make && this.form.model && this.form.yom);
    },
    canGenerateQuote() {
      if (this.form.cover_type === 'tpo') return !!(this.form.make && this.form.model && this.form.yom);
      if (this.yomForcesTPO) return false; // comprehensive not allowed for old vehicles
      return !!(this.form.make && this.form.model && this.form.yom && parseInt(this.form.value, 10) >= 500000);
    },
    hasLiveQuote() {
      return !!(this.quoteResponse && this.quoteResponse.quoteId);
    },
    quoteRiskPrem() {
      if (this.quoteResponse && this.quoteResponse.covertypes && this.quoteResponse.covertypes.length > 0) {
        const ids = COVER_TYPE_IDS[this.form.cover_type] || [];
        const risk = this.quoteResponse.covertypes.find((c) => (ids.includes(c.covertypeId) || ids.includes(c.car?.coverTypeId)));
        return (risk || this.quoteResponse.covertypes[0]).riskPremDto || {};
      }
      return null;
    },
    basicPremium() {
      return this.basePremium || 0;
    },
    levy() {
      if (!this.quoteRiskPrem) return 0;
      return this.quoteRiskPrem.trainingLevy || Math.round((0.2 / 100) * this.basicPremium);
    },
    phf() {
      if (!this.quoteRiskPrem) return 0;
      return this.quoteRiskPrem.phf || Math.ceil((0.25 / 100) * this.basicPremium);
    },
    totalPremium() {
      return this.manualTotalPremium;
    },
    totalSelectedBenefitsPremium() {
      return this.fetchedChargeableBenefits
        .filter((b) => this.form.addons_dynamic.includes(b.sectCode))
        .reduce((sum, b) => sum + this.getBenefitPremium(b), 0);
    },
    manualSubtotal() {
      return (this.basicPremium || 0) + (this.totalSelectedBenefitsPremium || 0);
    },
    manualLevy() {
      return Math.round(this.manualSubtotal * 0.002);
    },
    manualPHF() {
      return Math.ceil(this.manualSubtotal * 0.0025);
    },
    manualTotalPremium() {
      return this.manualSubtotal + this.manualLevy + this.manualPHF + 40;
    },
    benefitPremiumMap() {
      return this.benefitDetails.reduce((map, b) => {
        const result = { ...map };
        const code = b.sectCode || b.sectionCode;
        if (code) result[code] = b;
        return result;
      }, {});
    },
    liveVehicleName() {
      if (this.form.cover_type === 'tpo') {
        if (this.form.make && this.form.model) {
          return `${this.form.make} ${this.form.model}${this.form.yom ? ` - ${this.form.yom}` : ''} (Third Party Only)`;
        }
        return 'Third Party Only Cover';
      }
      if (this.form.make && this.form.model) {
        return `${this.form.make} ${this.form.model}${this.form.yom ? ` - ${this.form.yom}` : ''}`;
      }
      return 'Vehicle';
    },
    liveCoverLabel() {
      const types = { comprehensive: 'Comprehensive', tpo: 'TPO', tpf: COVER_TYPE_LABELS.tpf };
      const usage = this.form.usage_type === 'commercial' ? 'Commercial' : 'Private';
      return `${types[this.form.cover_type] || 'Insurance'} (${usage})`;
    },
    liveRateApplied() {
      if (this.form.cover_type !== 'comprehensive' || !this.quoteResponse?.covertypes) return null;
      const ids = COVER_TYPE_IDS.comprehensive || [];
      const risk = this.quoteResponse.covertypes.find((c) => ids.includes(c.covertypeId));
      const rate = risk?.rateApplied;
      if (rate == null || Number(rate) >= 100) return null;
      return Number(rate);
    },
    liveRateLabel() {
      const r = this.liveRateApplied;
      if (r == null) return null;
      const pct = Number.isInteger(r) ? r : Number(r.toFixed(1));
      return `${pct}% of sum insured`;
    },
  },
  mounted() {
    // ── One-time cleanup: remove old string-valued cache entries ──
    const mountedUser = this.$store?.getters?.[AUTH_USER_GETTER] || this.authenticatedUser;
    const mountedEmail = mountedUser?.email || '';
    purgeLegacyKeys(mountedEmail);

    this.getCarMakes();
    this.getProducts();
    this.fetchInitialBenefits();

    /**
     * State restoration removed to avoid confusion on calculation of quote.
     * The form now starts fresh every time this page is loaded.
     */
  },
  watch: {
    'form.make': async function loadModels(newVal) {
      if (!newVal) return;
      this.carMakeObj = this.carMakes.find((x) => x.makeDetails === newVal);
      this.form.model = '';
      if (this.carMakeObj) {
        await this.getCarModels(this.carMakeObj.vmkcode);
      }
      this.debounceRecalc();
    },
    'form.value': function onFormValueChanged() {
      this.debounceRecalc();
    },
    'form.model': function onFormModelChanged() {
      this.debounceRecalc();
    },
    'form.cover_type': function onCoverChange(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.benefitDetails = [];
        if (newVal === 'tpo') this.form.addons_dynamic = [];
        this.debounceRecalc();
      }
    },
    'form.yom': function onYomChanged(val) {
      if (!val) return;
      const age = new Date().getFullYear() - parseInt(val, 10);
      if (age > 12) {
        this.form.cover_type = 'tpo';
        this.benefitDetails = [];
        this.form.addons_dynamic = [];
      }
      this.debounceRecalc();
    },
  },
  beforeDestroy() {
    if (this.calcTimer) clearTimeout(this.calcTimer);
  },
  methods: {
    resetAndStartFresh() {
      trackAgentInteraction(this, 'underwriting_reset_quote');
      const user = this.$store?.getters?.[AUTH_USER_GETTER] || this.authenticatedUser;
      const email = user?.email || '';
      clearAllSaleData(email);

      // Reset all form state
      this.quoteResponse = null;
      this.benefitDetails = [];
      this.form = {
        cover_type: 'comprehensive',
        usage_type: 'private',
        commercial_usage: '',
        bodyType: '',
        make: '',
        model: '',
        yom: '',
        value: '',
        addons: [],
        addons_dynamic: [],
        benefit_custom_limits: {},
      };
      this.showResetConfirm = false;
      this.$notify.success('Quote cleared. Ready for a new quote.');
    },
    fetchInitialBenefits() {
      this.benefitsLoading = true;
      axios.get('/customer/gis/products/70/covertypes/101/chargeablebenefits', { _skipErrorRedirect: true })
        .then((res) => {
          this.fetchedChargeableBenefits = res.data.object || [];
        })
        .catch((err) => {
          console.error('Failed to fetch initial benefits', err);
        })
        .finally(() => {
          this.benefitsLoading = false;
        });
    },
    getCarMakes() {
      axios.get('/customer/gis/vehicles/make/retrieveAll', { _skipErrorRedirect: true }).then((response) => {
        this.carMakes = response.data.object;
      }).catch((err) => console.error('Failed to load car makes', err));
    },
    async getCarModels(id) {
      if (!id) return;
      await axios.get(`/customer/gis/vehicles/${id}/models`, { _skipErrorRedirect: true }).then((response) => {
        this.carModels = response.data.object;
      }).catch((err) => console.error('Failed to load car models', err));
    },
    getProducts() {
      axios.get('/customer/gis/products/productgroup/70/retrieveall', { _skipErrorRedirect: true }).then((response) => {
        this.products = response.data.object;
      }).catch((err) => console.error('Failed to load products', err));
    },

    async getAgentId() {
      const user = this.$store?.getters?.[AUTH_USER_GETTER] || this.authenticatedUser;
      const email = user?.email || '';

      const cached = loadAgentId(email);
      if (cached !== null) {
        return cached;
      }

      const agnCode = user?.['custom:agent_code'];
      if (!agnCode && !email) return 0;

      try {
        const baseUrl = '/customer/gis/agent/dashboard/summary';
        const url = agnCode
          ? `${baseUrl}?agn_code=${agnCode}`
          : `${baseUrl}?email=${encodeURIComponent(email)}`;

        const res = await axios.get(url, { validateStatus: () => true, _skipErrorRedirect: true });

        if (res.status === 200 && res.data) {
          // ── agent.code is the numeric agent ID ──
          const code = res.data.agent?.code;
          if (code !== null && code !== undefined) {
            const parsed = parseInt(String(code), 10);
            if (!Number.isNaN(parsed)) {
              saveAgentId(email, parsed);
              return parsed;
            }
          }
        }
      } catch (e) {
        console.error('[getAgentId] API call failed', e);
      }

      return 0;
    },

    debounceRecalc() {
      if (this.calcTimer) clearTimeout(this.calcTimer);
      this.calcTimer = setTimeout(() => {
        if (this.canGenerateQuote) this.fetchLiveQuote();
      }, 600);
    },

    buildQuoteCreatePayload() {
      const ids = COVER_TYPE_IDS[this.form.cover_type] || [104];
      const additionalBenefits = (this.fetchedChargeableBenefits || [])
        .filter((b) => (this.form.addons_dynamic || []).includes(b.sectCode))
        .map((b) => ({
          id: b.sectCode,
          benefit: b.sectDesc,
          value: this.form.benefit_custom_limits[b.sectCode] || null,
          noOfBeneficiaries: 0,
        }));

      return {
        ids,
        data: {
          origin: 'P',
          agentId: null,
          additionalBenefits,
          carDTOS: [
            {
              carId: 0,
              coverTypeId: ids[0] || 0,
              evaluationSelected: -1,
              hasInsurance: false,
              make: this.form.make,
              model: this.form.model,
              riskId: 0,
              seatingCapacity: 0,
              valueOfCar: this.form.cover_type === 'tpo' ? 0 : (parseInt(this.form.value, 10) || 0),
              bodyType: this.form.bodyType || '',
              valueOfEntertainmentScreen: 0,
              valueOfWindScreen: 0,
              vehicleImages: [],
              yearOfManufacture: this.form.yom,
            },
          ],
          prodId: 3437,
          email: this.isAuthenticated ? this.authenticatedUser.email : '',
          fullName: this.isAuthenticated ? this.authenticatedUser.name : '',
          kraPinNumber: this.isAuthenticated ? this.authenticatedUser['custom:kra-pin'] : '',
        },
      };
    },

    applyQuoteResponse(quoteData, ids) {
      this.quoteResponse = quoteData;
      const activeRisk = quoteData.covertypes.find(
        (c) => ids.includes(c.covertypeId) || ids.includes(c.car?.coverTypeId),
      ) || quoteData.covertypes[0];
      const riskPrem = activeRisk?.riskPremDto;
      if (riskPrem?.totalPremium) {
        this.basePremium = riskPrem.basicPremium || Math.round((riskPrem.totalPremium - 40) / 1.0045);
      }
    },

    async fetchLiveQuote() {
      if (!this.canGenerateQuote) return;
      this.liveRecalcLoading = true;

      const { ids, data } = this.buildQuoteCreatePayload();
      try {
        data.agentId = await this.getAgentId();
        const response = await axios.post('/customer/quotation/create', data, { _skipErrorRedirect: true });
        const quoteData = response.data?.data;
        if (!quoteData?.covertypes?.length) {
          throw new Error('Invalid quote response');
        }

        this.applyQuoteResponse(quoteData, ids);
        this.isAmendmentMode = false;

        if (this.form.cover_type === 'comprehensive' && this.form.addons_dynamic.length > 0) {
          try {
            await this.syncBenefitsWithQuote();
          } catch (err) {
            console.warn('Benefit sync after live recalc failed', err);
          }
        } else {
          this.benefitDetails = [];
        }
      } catch (err) {
        console.warn('Live quote recalc failed', err);
      } finally {
        this.liveRecalcLoading = false;
      }
    },

    async syncBenefitsWithQuote() {
      const selectedBenefits = this.fetchedChargeableBenefits
        .filter((b) => {
          if (!this.form.addons_dynamic.includes(b.sectCode)) return false;
          // For freeLimit benefits: only include in API call if custom > freeLimit
          if (b.freeLimit > 0) {
            const custom = this.form.benefit_custom_limits[b.sectCode];
            return custom && custom > b.freeLimit;
          }
          return true;
        })
        .map((b) => ({
          noOfBeneficiaries: 0,
          sectionCode: b.sectCode,
          sectionName: b.sectDesc,
          insuredAmount: this.form.benefit_custom_limits[b.sectCode] || b.insuredAmount || 0,
        }));

      const ids = COVER_TYPE_IDS[this.form.cover_type] || [];
      let activeRisk = null;

      if (this.quoteResponse) {
        if (this.quoteResponse.covertypes && this.quoteResponse.covertypes.length > 0) {
          activeRisk = this.quoteResponse.covertypes.find((c) => (ids.includes(c.covertypeId) || ids.includes(c.car?.coverTypeId))) || this.quoteResponse.covertypes[0];
        } else if (this.quoteResponse.quote && this.quoteResponse.quote.covertypes && this.quoteResponse.quote.covertypes.length > 0) {
          activeRisk = this.quoteResponse.quote.covertypes.find((c) => (ids.includes(c.covertypeId) || ids.includes(c.car?.coverTypeId))) || this.quoteResponse.quote.covertypes[0];
        }
      }

      const riskIdPayload = activeRisk ? (activeRisk.riskId || activeRisk.car?.riskId || 0) : 0;
      const payload = { benefits: selectedBenefits, riskId: riskIdPayload };

      return axios.post('/customer/quotation/benefit/change', payload, { _skipErrorRedirect: true })
        .then((response) => {
          const resData = response.data.data || response.data;

          if (resData.totalPremium !== undefined && activeRisk?.riskPremDto) {
            Vue.set(activeRisk.riskPremDto, 'totalPremium', resData.totalPremium);
          }

          this.benefitDetails = resData.benefits || [];
        }).catch((err) => {
          console.error('Benefit sync failed', err);
          this.$toast.error('Failed to synchronize benefits. Premium may be inaccurate.');
          throw err;
        });
    },

    handleBenefitToggle({ skipApiSync } = {}) {
      if (!this.quoteResponse) return;
      // Don't call API for free benefits with no custom limit
      if (skipApiSync) return;

      this.benefitUpdating = true;

      this.syncBenefitsWithQuote()
        .finally(() => {
          this.benefitUpdating = false;
        });
    },

    getBenefitPremium(benefit) {
      const customLimit = this.form.benefit_custom_limits[benefit.sectCode];
      const freeLimit = benefit.freeLimit || 0;

      // ── Phase 10: If it's a free limit benefit and NO custom limit over the free limit is set -> 0 ──
      if (freeLimit > 0 && (!customLimit || customLimit <= freeLimit)) {
        return 0;
      }

      if (benefit.rateType === 'Amount' || !benefit.divFactor) {
        const raw = (benefit.sectDesc || '').toUpperCase().trim();
        const isInfotainment = raw.includes('RADIO CASSETTE') || raw.includes('ENTERTAINMENT') || raw.includes('INFORTAINMENT');

        if (isInfotainment && this.basicPremium > 0) {
          return Math.ceil((benefit.rate / 100) * this.basicPremium);
        }
        return benefit.rate || benefit.minimumLimit || 0;
      }

      // Basis: custom limit OR vehicle value (fallback)
      const valToUse = customLimit
        || this.quoteResponse?.covertypes?.[0]?.car?.valueOfCar
        || parseInt(this.form.value, 10)
        || 0;

      const raw = (benefit.sectDesc || '').toUpperCase().trim();
      const isPVT = raw.includes('PVT') || raw.includes('POLITICAL VIOLENCE') || raw.includes('TERRORISM');
      const isExcess = raw.includes('EXCESS PROTECTOR') || raw.includes('EXCESS PROTECT') || raw.includes('E.P.P') || raw.includes('EPP');

      // For PVT/Excess Protector (percentage of vehicle value)
      if ((isPVT || isExcess) && valToUse) {
        const rate = benefit.rate || 0.25;
        const amount = Math.ceil((rate / 100) * valToUse);
        let min = 0;
        if (isPVT) min = 3000;
        else if (isExcess) min = 5000;
        return Math.max(amount, min);
      }

      // Percentage fallback (no freeLimit, no API value)
      if (benefit.divFactor > 1 && valToUse) {
        const chargeableValue = Math.max(valToUse - freeLimit, 0);
        const amount = Math.ceil((benefit.rate / benefit.divFactor) * chargeableValue);
        const min = benefit.minimumLimit || (benefit.sectCode === 1614 ? 5000 : 0);
        return Math.max(amount, min);
      }

      if (!valToUse) {
        return benefit.minimumLimit || (benefit.sectCode === 1614 ? 5000 : 0);
      }

      const chargeableValue = Math.max(valToUse - freeLimit, 0);
      const amount = Math.ceil((benefit.rate / benefit.divFactor) * chargeableValue);

      // Minimums
      const min = benefit.minimumLimit || (benefit.sectCode === 1614 ? 5000 : 0);
      return Math.max(amount, min);
    },

    /**
     * proceedToSale
     * Persists the latest quote to sessionStorage and navigates to AgentSale.
     * The quoteId is passed as a query param so AgentSale can hydrate from the
     * correct session key.
     */
    proceedToSale() {
      trackAgentInteraction(this, 'underwriting_proceed_to_sale', {
        quote_id: this.quoteResponse?.quoteId || 'unknown',
      });
      if (this.updatingBenefits) {
        this.$toast.info('Please wait for benefits to synchronize...');
        return;
      }
      if (this.syncTimer) {
        // Force immediate sync if user clicks proceed during debounce
        clearTimeout(this.syncTimer);
        this.syncTimer = null;
        this.syncBenefitsWithQuote().then(() => this.proceedToSale());
        return;
      }

      const user = this.$store?.getters?.[AUTH_USER_GETTER] || this.authenticatedUser;
      const email = user?.email || '';
      if (this.quoteResponse) {
        // Enforce premium parity by saving the exact total shown to the user
        const payload = {
          ...this.quoteResponse,
          confirmedTotal: this.manualTotalPremium,
        };
        savePendingQuote(email, payload);
      }
      if (this.quoteResponse && this.quoteResponse.quoteId) {
        this.$router.push({ name: 'AgentSale', query: { quoteId: this.quoteResponse.quoteId } });
      } else {
        this.$router.push({ name: 'AgentSale' });
      }
    },

    formatCurrency(val) {
      return new Intl.NumberFormat().format(Math.round(val || 0));
    },

    async buildQuotePDF(doc, { displayName = '', displayEmail = '' } = {}) {
      const clientDisplayName = displayName || this.customerName || 'N/A';
      const clientDisplayEmail = displayEmail || this.customerEmail || 'N/A';
      const agentName = this.authenticatedUser?.name || 'N/A';

      const quoteId = this.quoteResponse.quoteId
        ?? this.quoteResponse.quote?.quoteId
        ?? this.quoteResponse.covertypes?.[0]?.quoteId;

      const pageW = doc.internal.pageSize.getWidth();
      // eslint-disable-next-line no-unused-vars
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentW = pageW - margin * 2;
      let y = 10;

      const blue = [30, 66, 118];
      const cyan = [0, 174, 239];
      const gray = [100, 116, 139];
      const lightBlue = [235, 245, 255];

      const footerH = 25; // Estimate for validity + strip + footer block
      const pageHMinusMargin = pageH - margin;

      const checkPageBreak = (needed) => {
        if (y + needed > pageHMinusMargin) {
          doc.addPage();
          y = 10;
          // Re-render minimal header on new page
          try {
            doc.addImage('/pacis-logo-tagline.png', 'PNG', margin, y, 38, 14);
          } catch (e) { /* ignore */ }
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...blue);
          doc.text('MOTOR INSURANCE INVOICE (CONTINUED)', pageW - margin, y + 8, { align: 'right' });
          y += 20;
          doc.setDrawColor(...blue);
          doc.setLineWidth(0.6);
          doc.line(margin, y, pageW - margin, y);
          y += 10;
          return true;
        }
        return false;
      };

      // ── Invoice meta ──
      const invoiceNo = `INV-${String(this.quoteResponse?.quoteId || '').padStart(6, '0')}`;

      // ── Logo ──
      try {
        doc.addImage('/pacis-logo-tagline.png', 'PNG', margin, y, 38, 14);
      } catch (e) {
        // Logo failed
      }

      // ── Insurer info (right-aligned) ──
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('Pacis Insurance Company Limited', pageW - margin, y + 4, { align: 'right' });
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text('Pacis Centre, 4th Floor, off Waiyaki Way  |  P.O BOX 1870-00200, Nairobi', pageW - margin, y + 8.5, { align: 'right' });
      doc.text('Email: info@paciskenya.com  |  Tel: +254 706 610 000', pageW - margin, y + 12.5, { align: 'right' });
      // Agent business name
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...cyan);
      doc.text(`INTERMEDIARY: ${agentName}`, pageW - margin, y + 17, { align: 'right' });

      y += 20;

      // ── Full-width divider line ──
      doc.setDrawColor(...blue);
      doc.setLineWidth(0.6);
      doc.line(margin, y, pageW - margin, y);
      y += 5;

      // ── Document title & date (plain text, no colored band) ──
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('MOTOR INSURANCE INVOICE', margin, y);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text(
        `Date: ${new Date().toLocaleDateString('en-KE', { day: '2-digit', month: 'long', year: 'numeric' })}`,
        pageW - margin,
        y,
        { align: 'right' },
      );
      y += 8;

      // ── Thin accent line under title ──
      doc.setDrawColor(...cyan);
      doc.setLineWidth(0.4);
      doc.line(margin, y, pageW - margin, y);
      y += 7;

      // ── Invoice meta row (Invoice # left, Bill To right) ──
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('INVOICE NO.', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text(invoiceNo, margin + 28, y);

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('BILL TO:', pageW - margin - 60, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text(displayName || 'N/A', pageW - margin - 40, y);
      y += 5;

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('ISSUED:', margin, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text(new Date().toLocaleDateString('en-KE', { day: '2-digit', month: 'long', year: 'numeric' }), margin + 28, y);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text(displayEmail || 'N/A', pageW - margin - 40, y);
      y += 8;

      // Thin divider
      doc.setDrawColor(...cyan);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageW - margin, y);
      y += 6;

      // ── Two-column info boxes ──
      const boxH = 30;
      const colW = (contentW - 6) / 2;

      // Left — Client
      doc.setFillColor(...lightBlue);
      doc.roundedRect(margin, y, colW, boxH, 2, 2, 'F');
      doc.setDrawColor(210, 225, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, colW, boxH, 2, 2, 'S');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('CLIENT DETAILS', margin + 3, y + 5);

      const clientRows = [
        ['Name', clientDisplayName],
        ['Email', clientDisplayEmail],
        ['Quote ID', String(quoteId ?? 'N/A')],
      ];
      clientRows.forEach(([key, val], i) => {
        const ry = y + 11 + i * 6;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...gray);
        doc.text(key, margin + 3, ry);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text(val, margin + 22, ry);
      });

      // Right — Vehicle
      const rx = margin + colW + 6;
      doc.setFillColor(...lightBlue);
      doc.roundedRect(rx, y, colW, boxH, 2, 2, 'F');
      doc.setDrawColor(210, 225, 240);
      doc.roundedRect(rx, y, colW, boxH, 2, 2, 'S');

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('VEHICLE DETAILS', rx + 3, y + 5);

      const coverTypeLabels = COVER_TYPE_LABELS;
      const vehicleRows = [
        ['Make / Model', `${this.form.make} ${this.form.model}`],
        ['Year', this.form.yom],
        ['Cover Type', coverTypeLabels[this.form.cover_type] || 'N/A'],
      ];
      if (this.form.cover_type !== 'tpo') {
        vehicleRows.push(['Insured Value', `Ksh ${this.formattedValue}`]);
      }
      vehicleRows.forEach(([key, val], i) => {
        const ry = y + 11 + i * 5.5;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...gray);
        doc.text(key, rx + 3, ry);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text(val, rx + 26, ry);
      });

      y += boxH + 6;

      // ── Intermediary strip ──
      doc.setFillColor(235, 245, 255);
      doc.setDrawColor(210, 225, 240);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, contentW, 8, 2, 2, 'FD');
      // Cyan left accent bar
      doc.setFillColor(...cyan);
      doc.rect(margin, y, 3, 8, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('BILLED VIA — INTERMEDIARY', margin + 6, y + 3.2);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(`Name: ${agentName}`, margin + 6, y + 6.2);

      y += 14;

      // ── PREMIUM SUMMARY heading ──
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('INVOICE SUMMARY', margin, y);
      y += 3;
      doc.setDrawColor(...blue);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageW - margin, y);
      y += 4;

      // Table header row
      doc.setFillColor(...lightBlue);
      doc.rect(margin, y, contentW, 7, 'F');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('Item Description', margin + 3, y + 5);
      doc.text('Amount (Ksh)', pageW - margin - 3, y + 5, { align: 'right' });
      y += 7;

      // Row helper
      const addRow = (label, amount, isDim = false) => {
        doc.setDrawColor(235, 240, 248);
        doc.setLineWidth(0.2);
        doc.line(margin, y, pageW - margin, y);
        doc.setFontSize(isDim ? 8 : 9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...(isDim ? gray : blue));
        doc.text(label, margin + 3, y + 5);
        doc.text(
          typeof amount === 'number' ? this.formatCurrency(amount) : String(amount),
          pageW - margin - 3,
          y + 5,
          { align: 'right' },
        );
        y += 7;
      };

      // ── Pull premium values directly from quoteResponse to avoid 0s ──
      const riskPrem = this.quoteResponse?.covertypes?.[0]?.riskPremDto || {};
      const basicPrem = riskPrem.basicPremium || this.basicPremium || 0;

      const coverTypeName = this.form.cover_type === 'tpo' ? 'TPO Flat Rate — Motor Insurance' : 'Motor Insurance Premium';
      addRow(coverTypeName, basicPrem);

      // Use getBenefitPremium (same as sidebar) so PDF always matches displayed estimate
      let benefitTotal = 0;
      this.fetchedChargeableBenefits
        .filter((b) => this.form.addons_dynamic.includes(b.sectCode))
        .forEach((b) => {
          const bPrem = this.getBenefitPremium(b);
          if (bPrem <= 0) return;
          const name = (b.sectDesc || 'Benefit').toLowerCase();
          addRow(name.charAt(0).toUpperCase() + name.slice(1), bPrem);
          benefitTotal += bPrem;
        });

      const subtotal = basicPrem + benefitTotal;
      const computedLevy = Math.round(subtotal * 0.002);
      const computedPhf = Math.ceil(subtotal * 0.0025);
      const computedTotal = subtotal + computedLevy + computedPhf + 40;

      addRow('Training Levy (0.2%)', computedLevy, true);
      addRow('PHF (0.25%)', computedPhf, true);
      addRow('Stamp Duty', 40, true);

      // ── Grand Total row ──
      doc.setFillColor(...lightBlue);
      doc.rect(margin, y, contentW, 11, 'F');
      doc.setDrawColor(...blue);
      doc.setLineWidth(0.6);
      doc.line(margin, y, pageW - margin, y);
      doc.line(margin, y + 11, pageW - margin, y + 11);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text('GRAND TOTAL', margin + 3, y + 7.5);
      doc.setFontSize(12);
      doc.text(`Ksh ${this.formatCurrency(computedTotal)}`, pageW - margin - 3, y + 7.5, { align: 'right' });
      y += 20;

      // ── Payment Options (Comprehensive only) ──
      if (this.form.cover_type === 'comprehensive') {
        // Section label with pill background
        doc.setFillColor(245, 248, 252);
        doc.roundedRect(margin, y, contentW, 8, 2, 2, 'F');
        doc.setDrawColor(210, 225, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, 8, 2, 2, 'S');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text('PAYMENT OPTIONS (INSTALLMENTS)', margin + 6, y + 5.5);
        // Cyan left accent bar
        doc.setFillColor(...cyan);
        doc.rect(margin, y, 3, 8, 'F');
        y += 13;

        // Installment rows with alternating subtle background
        const installments = [
          ['Full Payment (Now)', computedTotal],
          ['Installment 1 (40% Now)', computedTotal * 0.4],
          ['Installment 2 (Month 2)', computedTotal * 0.3],
          ['Installment 3 (Month 3)', computedTotal * 0.3],
        ];

        installments.forEach(([label, amount], i) => {
          if (i % 2 === 0) {
            doc.setFillColor(250, 252, 255);
            doc.rect(margin, y, contentW, 10, 'F');
          }
          doc.setDrawColor(235, 240, 248);
          doc.setLineWidth(0.2);
          doc.line(margin, y + 10, pageW - margin, y + 10);

          // Cyan bullet dot
          doc.setFillColor(...cyan);
          doc.circle(margin + 5, y + 5, 1.2, 'F');

          doc.setFontSize(9);
          doc.setFont('helvetica', i === 0 ? 'bold' : 'normal');
          doc.setTextColor(...(i === 0 ? blue : gray));
          doc.text(label, margin + 10, y + 6);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...blue);
          doc.text(this.formatCurrency(amount), pageW - margin - 3, y + 6, { align: 'right' });
          y += 10;
        });

        y += 8;
      }

      // ── Included Benefits (Comprehensive only) ──
      if (this.form.cover_type === 'comprehensive') {
        // Section label
        doc.setFillColor(235, 245, 255);
        doc.setDrawColor(210, 225, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, 8, 2, 2, 'FD');
        doc.setFillColor(...cyan);
        doc.rect(margin, y, 3, 8, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text('BENEFITS INCLUDED IN YOUR COVER', margin + 6, y + 5.5);
        y += 13;

        // Top 4 benefit cards (2x2 grid)
        const topBenefits = [
          { title: 'Towing', limit: BENEFIT_FREE_LIMIT },
          { title: 'Entertainment', limit: BENEFIT_FREE_STD_LIMIT },
          { title: 'Windscreen', limit: BENEFIT_FREE_STD_LIMIT },
          { title: 'Damages Repair', limit: BENEFIT_FREE_LIMIT },
        ];
        const cardW = (contentW - 6) / 4;
        topBenefits.forEach((b, i) => {
          const cx = margin + i * (cardW + 2);
          doc.setFillColor(248, 251, 255);
          doc.setDrawColor(210, 225, 240);
          doc.setLineWidth(0.3);
          doc.roundedRect(cx, y, cardW, 18, 2, 2, 'FD');
          doc.setFontSize(7.5);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...blue);
          doc.text(b.title, cx + cardW / 2, y + 7, { align: 'center' });
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...cyan);
          const limitLines = doc.splitTextToSize(b.limit, cardW - 4);
          doc.text(limitLines, cx + cardW / 2, y + 12, { align: 'center' });
        });
        // Check space for "Other Benefits" (approx 10 header + 7 per row)
        const estH = 10 + (OTHER_BENEFITS_PDF.length * 7) + 10;
        checkPageBreak(estH);

        // Other benefits table
        const otherBenefits = OTHER_BENEFITS_PDF;

        // Table header
        doc.setFillColor(...lightBlue);
        doc.rect(margin, y, contentW, 6, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text('Other Benefits', margin + 3, y + 4.2);
        doc.text('Limit', pageW - margin - 3, y + 4.2, { align: 'right' });
        y += 6;

        otherBenefits.forEach(([title, limit], i) => {
          if (i % 2 === 0) {
            doc.setFillColor(250, 252, 255);
            doc.rect(margin, y, contentW, 7, 'F');
          }
          doc.setDrawColor(235, 240, 248);
          doc.setLineWidth(0.2);
          doc.line(margin, y + 7, pageW - margin, y + 7);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...blue);
          doc.text(title, margin + 3, y + 5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...cyan);
          doc.text(limit, pageW - margin - 3, y + 5, { align: 'right' });
          y += 7;
        });

        y += 6;
      }

      // ── Statutory Benefits (TPO only) ──
      if (this.form.cover_type === 'tpo') {
        // Section label
        doc.setFillColor(235, 245, 255);
        doc.setDrawColor(210, 225, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, 8, 2, 2, 'FD');
        doc.setFillColor(...cyan);
        doc.rect(margin, y, 3, 8, 'F');
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text('STATUTORY BENEFITS INCLUDED', margin + 6, y + 5.5);
        y += 13;

        // Table header
        doc.setFillColor(...lightBlue);
        doc.rect(margin, y, contentW, 6, 'F');
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...blue);
        doc.text('Benefit', margin + 3, y + 4.2);
        doc.text('Cover', margin + 80, y + 4.2);
        doc.text('Limit', pageW - margin - 3, y + 4.2, { align: 'right' });
        y += 6;

        const tpoRows = [
          ['Third Party Person Liability', 'Any One Person', 'KSH 3,000,000', cyan],
          ['Passengers Liability', 'Any One Person', 'KSH 4,000,000', [56, 161, 105]],
          ['', 'Any One Event', 'KSH 20,000,000', [56, 161, 105]],
          ['Third Party Property Damage', 'Per Event', 'KSH 5,000,000', [197, 48, 48]],
        ];

        tpoRows.forEach(([title, cover, limit, limitColor], i) => {
          if (i % 2 === 0) {
            doc.setFillColor(250, 252, 255);
            doc.rect(margin, y, contentW, 7, 'F');
          }
          doc.setDrawColor(235, 240, 248);
          doc.setLineWidth(0.2);
          doc.line(margin, y + 7, pageW - margin, y + 7);

          if (title) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(...blue);
            doc.text(title, margin + 3, y + 5);
          }

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...gray);
          doc.text(cover, margin + 80, y + 5);

          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...limitColor);
          doc.text(limit, pageW - margin - 3, y + 5, { align: 'right' });
          y += 7;
        });

        // Flat rate note
        y += 4;
        doc.setFillColor(255, 251, 235);
        doc.setDrawColor(253, 230, 138);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, contentW, 8, 2, 2, 'FD');
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(146, 64, 14);
        doc.text(
          'TPO is charged at a flat statutory rate — no vehicle value required.',
          margin + 3, y + 5.5,
        );
        y += 14;
      }

      // ── Space check for footer elements ──
      checkPageBreak(footerH);

      // ── Validity strip ──
      doc.setFillColor(255, 251, 235);
      doc.setDrawColor(253, 230, 138);
      doc.setLineWidth(0.3);
      doc.roundedRect(margin, y, contentW, 9, 2, 2, 'FD');
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(146, 64, 14);
      doc.text(
        'Quote is only valid for 30 days. Cover commences only upon receipt of full premium. This is a computer-generated invoice.',
        margin + 3,
        y + 5.8,
      );
      y += 14;

      // ── Cyan accent bottom strip ──
      doc.setFillColor(...cyan);
      doc.rect(margin, y, contentW, 1.2, 'F');
      y += 6;

      // ── Footer ──
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      doc.text('Pacis Insurance Company Limited is regulated by the Insurance Regulatory Authority of Kenya.', margin, y);
      y += 4;
      doc.text('This is a computer-generated document and does not require a signature.', margin, y);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...blue);
      doc.text(`Quotation — ${new Date().toLocaleDateString('en-KE')}`, pageW - margin, y, { align: 'right' });
    },

    async sendEmailQuote() {
      if (!this.quoteResponse) return;

      this.customerName = sanitizeAlpha(this.customerName, 50);
      this.customerEmail = sanitizeEmail(this.customerEmail);

      if (!validateEmail(this.customerEmail)) {
        this.$notify.error('Please enter a valid email address.', 'Invalid Email');
        return;
      }

      this.sendingEmail = true;

      try {
        const quoteId = this.quoteResponse.quoteId
          ?? this.quoteResponse.quote?.quoteId
          ?? this.quoteResponse.covertypes?.[0]?.quoteId
          ?? 'N/A';

        const agentName = this.authenticatedUser?.name || 'N/A';
        const coverLabel = COVER_TYPE_LABELS[this.form.cover_type] || 'Motor Insurance';
        const vehicleStr = `${this.form.make} ${this.form.model}`;
        const dateStr = new Date().toLocaleDateString('en-KE', { day: '2-digit', month: 'long', year: 'numeric' });

        // ── Pull values same way buildQuotePDF does ──
        const riskPrem = this.quoteResponse?.covertypes?.[0]?.riskPremDto || {};
        const basicPrem = riskPrem.basicPremium || this.basicPremium || 0;

        // ── Benefit rows ──
        // ── Benefit rows — use getBenefitPremium (same as sidebar) ──
        let emailBenefitTotal = 0;
        const benefitRowsHtml = this.fetchedChargeableBenefits
          .filter((b) => this.form.addons_dynamic.includes(b.sectCode))
          .map((b) => {
            const bPrem = this.getBenefitPremium(b);
            if (bPrem <= 0) return '';
            emailBenefitTotal += bPrem;
            const name = (b.sectDesc || 'Benefit').toLowerCase();
            const nameFmt = name.charAt(0).toUpperCase() + name.slice(1);
            return `
            <tr>
              <td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #f1f5f9;color:#1e293b;">${nameFmt}</td>
              <td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #f1f5f9;text-align:right;color:#1e293b;">${this.formatCurrency(bPrem)}</td>
            </tr>`;
          }).join('');

        const emailSubtotal = basicPrem + emailBenefitTotal;
        const emailLevy = Math.round(emailSubtotal * 0.002);
        const emailPhf = Math.ceil(emailSubtotal * 0.0025);
        const emailTotal = emailSubtotal + emailLevy + emailPhf + 40;

        // ── Insured value row (not for TPO) ──
        const insuredValueRow = this.form.cover_type !== 'tpo' ? `
          <tr>
            <td style="padding:4px 10px;font-size:10px;color:#64748b;width:42%;">Insured Value</td>
            <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">Ksh ${this.formattedValue}</td>
          </tr>` : '';

        // ── Installment rows (comprehensive only) ──
        const installmentRowsHtml = this.form.cover_type === 'comprehensive' ? `
          <tr>
            <td colspan="2" style="padding:0;background:#f8fafc;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="padding:8px 20px 5px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;border-left:3px solid #00AEEF;">
                    PAYMENT OPTIONS (INSTALLMENTS)
                  </td>
                </tr>
                <tr>
                  <td style="padding:0 14px 8px;">
                    <table width="100%" cellpadding="0" cellspacing="0">
                      ${[
    ['Full Payment (Now)', emailTotal, true],
    ['Installment 1 (40% Now)', emailTotal * 0.4, false],
    ['Installment 2 (Month 2)', emailTotal * 0.3, false],
    ['Installment 3 (Month 3)', emailTotal * 0.3, false],
  ].map(([label, amount, bold], i) => `
                        <tr style="${i % 2 === 0 ? 'background:#fafcff;' : ''}">
                          <td style="padding:5px 6px;font-size:11px;color:${bold ? '#1E4276' : '#64748b'};font-weight:${bold ? '700' : '400'};">
                            <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#00AEEF;margin-right:6px;vertical-align:middle;"></span>
                            ${label}
                          </td>
                          <td style="padding:5px 6px;font-size:11px;text-align:right;color:#1E4276;font-weight:${bold ? '700' : '400'};">
                            ${this.formatCurrency(amount)}
                          </td>
                        </tr>`).join('')}
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>` : '';

        const comprehensiveBenefitsHtml = this.form.cover_type === 'comprehensive' ? `
    <!-- ══ INCLUDED BENEFITS ══ -->
    <tr>
      <td style="padding:0 20px 12px;">
        <div style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #1E4276;padding-bottom:3px;margin-bottom:8px;">
          ✦ BENEFITS INCLUDED IN YOUR COVER
        </div>

        <!-- Top 4 benefit cards row -->
        <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:10px;">
          <tr>
            ${[
    { title: 'Towing', limit: BENEFIT_FREE_LIMIT },
    { title: 'Entertainment', limit: BENEFIT_FREE_STD_LIMIT },
    { title: 'Windscreen', limit: BENEFIT_FREE_STD_LIMIT },
    { title: 'Damages Repair', limit: BENEFIT_FREE_LIMIT },
  ].map((b) => `
            <td style="width:25%;padding:0 4px 0 0;vertical-align:top;">
              <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;text-align:center;">
                <tr>
                  <td style="padding:16px 6px 3px;font-size:9px;font-weight:700;color:#1E4276;">${b.title}</td>
                </tr>
                <tr>
                  <td style="padding:0 6px 12px;font-size:8px;color:#00AEEF;line-height:1.3;">${b.limit}</td>
                </tr>
              </table>
            </td>`).join('')}
          </tr>
        </table>

        <!-- Other benefits rows -->
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
          <tr style="background:#EBF5FF;">
            <td colspan="2" style="padding:5px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.4px;border-bottom:1px solid #e2e8f0;">
              Other Benefits
            </td>
          </tr>
          ${OTHER_BENEFITS_EMAIL.map((b, i) => `
          <tr style="${i % 2 === 0 ? 'background:#fafcff;' : 'background:white;'}">
            <td style="padding:6px 10px;font-size:10px;font-weight:700;color:#1E4276;width:45%;border-bottom:1px solid #f1f5f9;">
              ${b.title}
            </td>
            <td style="padding:6px 10px;font-size:10px;color:#00AEEF;font-weight:600;border-bottom:1px solid #f1f5f9;">
              ${b.limit}
            </td>
          </tr>`).join('')}
        </table>
      </td>
    </tr>` : '';

        const tpoBenefitsHtml = this.form.cover_type === 'tpo' ? `
    <!-- ══ TPO STATUTORY BENEFITS ══ -->
    <tr>
      <td style="padding:0 20px 12px;">
        <div style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #1E4276;padding-bottom:3px;margin-bottom:8px;">
          ✦ STATUTORY BENEFITS INCLUDED
        </div>
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
          <!-- Header -->
          <tr style="background:#EBF5FF;">
            <td style="padding:5px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.4px;border-bottom:1px solid #e2e8f0;width:55%;">Benefit</td>
            <td style="padding:5px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.4px;border-bottom:1px solid #e2e8f0;">Cover</td>
            <td style="padding:5px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.4px;border-bottom:1px solid #e2e8f0;text-align:right;">Limit</td>
          </tr>
          <!-- Row 1 -->
          <tr style="background:white;">
            <td style="padding:8px 10px;font-size:10px;font-weight:700;color:#1E4276;border-bottom:1px solid #f1f5f9;">
              Third Party Person Liability
            </td>
            <td style="padding:8px 10px;font-size:10px;color:#64748b;border-bottom:1px solid #f1f5f9;">Any One Person</td>
            <td style="padding:8px 10px;font-size:10px;font-weight:700;color:#0077aa;text-align:right;border-bottom:1px solid #f1f5f9;">
              <span style="background:rgba(0,174,239,0.1);padding:2px 8px;border-radius:10px;">KSH 3,000,000</span>
            </td>
          </tr>
          <!-- Row 2a -->
          <tr style="background:#fafcff;">
            <td style="padding:8px 10px;font-size:10px;font-weight:700;color:#1E4276;border-bottom:1px solid #f1f5f9;" rowspan="2">
              Passengers Liability
            </td>
            <td style="padding:8px 10px;font-size:10px;color:#64748b;border-bottom:1px solid #f1f5f9;">Any One Person</td>
            <td style="padding:8px 10px;font-size:10px;font-weight:700;color:#276749;text-align:right;border-bottom:1px solid #f1f5f9;">
              <span style="background:rgba(56,161,105,0.1);padding:2px 8px;border-radius:10px;">KSH 4,000,000</span>
            </td>
          </tr>
          <!-- Row 2b -->
          <tr style="background:#fafcff;">
            <td style="padding:4px 10px;font-size:10px;color:#64748b;border-bottom:1px solid #f1f5f9;">Any One Event</td>
            <td style="padding:4px 10px;font-size:10px;font-weight:700;color:#276749;text-align:right;border-bottom:1px solid #f1f5f9;">
              <span style="background:rgba(56,161,105,0.1);padding:2px 8px;border-radius:10px;">KSH 20,000,000</span>
            </td>
          </tr>
          <!-- Row 3 -->
          <tr style="background:white;">
            <td style="padding:8px 10px;font-size:10px;font-weight:700;color:#1E4276;">
              Third Party Property Damage
            </td>
            <td style="padding:8px 10px;font-size:10px;color:#64748b;">Per Event</td>
            <td style="padding:8px 10px;font-size:10px;font-weight:700;color:#C53030;text-align:right;">
              <span style="background:rgba(229,62,62,0.1);padding:2px 8px;border-radius:10px;">KSH 5,000,000</span>
            </td>
          </tr>
        </table>
        <!-- Flat rate note -->
        <div style="margin-top:8px;padding:7px 12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;font-size:10px;color:#92400E;display:flex;align-items:center;gap:6px;">
          ⓘ TPO is charged at a flat statutory rate — no vehicle value required.
        </div>
      </td>
    </tr>` : '';

        const emailBody = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
</head>
<body style="margin:0;padding:0;background:#f1f5f9;font-family:'Helvetica Neue',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f1f5f9;padding:28px 0;">
  <tr><td align="center">
  <table width="620" cellpadding="0" cellspacing="0" style="max-width:620px;width:100%;background:#ffffff;border-radius:4px;overflow:hidden;border:1px solid #e2e8f0;">

    <!-- ══ LETTERHEAD ══ -->
    <tr>
      <td style="padding:16px 20px 12px;border-bottom:2px solid #1E4276;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="width:50%;">
              <img src="https://www.pacisinsurance.com/wp-content/uploads/2023/08/logo-PACIS-INSURANCE-tagline-1.png" style="height:35px;display:block;" alt="Pacis Logo" />
            </td>
            <td style="text-align:right;vertical-align:top;">
              <div style="font-size:10px;font-weight:700;color:#1E4276;">Pacis Insurance Company Limited</div>
              <div style="font-size:9px;color:#64748b;line-height:1.5;">Pacis Centre, 4th Floor, off Waiyaki Way &nbsp;|&nbsp; P.O BOX 1870-00200, Nairobi</div>
              <div style="font-size:9px;color:#64748b;line-height:1.5;">Email: info@paciskenya.com &nbsp;|&nbsp; Tel: +254 706 610 000</div>
              <div style="font-size:9px;font-weight:700;color:#00AEEF;margin-top:2px;">INTERMEDIARY: ${agentName}</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <!-- ══ TITLE BAND ══ -->
    <tr>
      <td style="background:#1E4276;padding:8px 20px;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="font-size:12px;font-weight:800;color:#ffffff;letter-spacing:0.4px;">MOTOR INSURANCE INVOICE</td>
            <td style="text-align:right;font-size:9px;color:rgba(255,255,255,0.7);">Date: ${dateStr}</td>
          </tr>
        </table>
      </td>
    </tr>

    <!-- ══ TWO-COLUMN INFO ══ -->
    <tr>
      <td style="padding:12px 20px;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <!-- Left: Client -->
            <td style="width:49%;vertical-align:top;border:1px solid #e2e8f0;border-radius:4px;overflow:hidden;">
              <div style="background:#EBF5FF;padding:4px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #e2e8f0;">BILL TO</div>
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr style="border-bottom:1px solid #f1f5f9;">
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;width:38%;">Name</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">${this.customerName || 'N/A'}</td>
                </tr>
                <tr style="border-bottom:1px solid #f1f5f9;">
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;">Email</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">${this.customerEmail}</td>
                </tr>
                <tr>
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;">Invoice No.</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;font-family:monospace;">INV-${String(quoteId).padStart(6, '0')}</td>
                </tr>
                <tr>
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;">Date Issued</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">${new Date().toLocaleDateString('en-KE', { day: '2-digit', month: 'long', year: 'numeric' })}</td>
                </tr>
              </table>
            </td>
            <td style="width:2%;"></td>
            <!-- Right: Vehicle -->
            <td style="width:49%;vertical-align:top;border:1px solid #e2e8f0;border-radius:4px;overflow:hidden;">
              <div style="background:#EBF5FF;padding:4px 10px;font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;border-bottom:1px solid #e2e8f0;">VEHICLE DETAILS</div>
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr style="border-bottom:1px solid #f1f5f9;">
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;width:42%;">Make / Model</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">${vehicleStr}</td>
                </tr>
                <tr style="border-bottom:1px solid #f1f5f9;">
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;">Year</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">${this.form.yom}</td>
                </tr>
                <tr style="border-bottom:1px solid #f1f5f9;">
                  <td style="padding:4px 10px;font-size:10px;font-weight:600;color:#64748b;">Cover Type</td>
                  <td style="padding:4px 10px;font-size:11px;font-weight:700;color:#1E4276;">${coverLabel}</td>
                </tr>
                ${insuredValueRow}
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <!-- ══ INTERMEDIARY STRIP ══ -->
    <tr>
      <td style="padding:0 20px 12px;">
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:4px;overflow:hidden;">
          <tr>
            <td style="background:#EBF5FF;border-left:3px solid #00AEEF;padding:5px 10px;">
              <span style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;">BILLED VIA — INTERMEDIARY</span>
              &nbsp;&nbsp;
              <span style="font-size:10px;color:#64748b;">Name: <strong style="color:#1E4276;">${agentName}</strong></span>
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <!-- ══ PREMIUM SUMMARY ══ -->
    <tr>
      <td style="padding:0 20px 12px;">
        <div style="font-size:9px;font-weight:800;color:#1E4276;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #1E4276;padding-bottom:3px;margin-bottom:6px;">INVOICE SUMMARY</div>
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #e2e8f0;border-radius:4px;overflow:hidden;">
          <!-- Table header -->
          <tr style="background:#EBF5FF;">
            <th style="padding:5px 10px;font-size:9px;font-weight:700;color:#1E4276;text-transform:uppercase;letter-spacing:0.4px;text-align:left;border-bottom:2px solid #1E4276;">Item Description</th>
            <th style="padding:5px 10px;font-size:9px;font-weight:700;color:#1E4276;text-transform:uppercase;letter-spacing:0.4px;text-align:right;border-bottom:2px solid #1E4276;">Amount (Ksh)</th>
          </tr>
          <!-- Basic Premium -->
          <tr>
            <td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #f1f5f9;color:#1e293b;">
              ${this.form.cover_type === 'tpo' ? 'TPO Flat Rate — Motor Insurance' : 'Motor Insurance Premium'}
            </td>
            <td style="padding:5px 10px;font-size:11px;border-bottom:1px solid #f1f5f9;text-align:right;color:#1e293b;">
              ${this.formatCurrency(basicPrem)}
            </td>
          </tr>
          <!-- Benefit rows -->
          ${benefitRowsHtml}
          <!-- Levies -->
          <tr class="subtotal">
            <td style="padding:5px 10px;font-size:10px;border-bottom:1px solid #f1f5f9;color:#64748b;">Training Levy (0.2%)</td>
            <td style="padding:5px 10px;font-size:10px;border-bottom:1px solid #f1f5f9;text-align:right;color:#64748b;">${this.formatCurrency(emailLevy)}</td>
          </tr>
          <tr>
            <td style="padding:5px 10px;font-size:10px;border-bottom:1px solid #f1f5f9;color:#64748b;">PHF (0.25%)</td>
            <td style="padding:5px 10px;font-size:10px;border-bottom:1px solid #f1f5f9;text-align:right;color:#64748b;">${this.formatCurrency(emailPhf)}</td>
          </tr>
          <tr>
            <td style="padding:5px 10px;font-size:10px;border-bottom:2px solid #1E4276;color:#64748b;">Stamp Duty</td>
            <td style="padding:5px 10px;font-size:10px;border-bottom:2px solid #1E4276;text-align:right;color:#64748b;">40</td>
          </tr>
          <!-- Grand Total -->
          <tr style="background:#EBF5FF;">
            <td style="padding:7px 10px;font-size:13px;font-weight:800;color:#1E4276;">GRAND TOTAL</td>
            <td style="padding:7px 10px;font-size:13px;font-weight:800;color:#1E4276;text-align:right;">Ksh ${this.formatCurrency(emailTotal)}</td>
          </tr>
          <!-- Installments -->
          ${installmentRowsHtml}
        </table>
      </td>
    </tr>

    ${comprehensiveBenefitsHtml}${tpoBenefitsHtml}

    <!-- ══ VALIDITY STRIP ══ -->
    <tr>
      <td style="padding:0 20px 16px;">
        <table width="100%" cellpadding="0" cellspacing="0" style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:4px;">
          <tr>
            <td style="padding:8px 12px;font-size:10px;color:#92400E;line-height:1.5;">
              Quote is only valid for 30 days. Cover commences only upon receipt of full premium. This is a computer-generated invoice.
            </td>
          </tr>
        </table>
      </td>
    </tr>

    <!-- ══ CYAN ACCENT LINE ══ -->
    <tr>
      <td style="padding:0 20px 10px;">
        <div style="height:3px;background:#00AEEF;border-radius:2px;"></div>
      </td>
    </tr>

    <!-- ══ FOOTER ══ -->
    <tr>
      <td style="padding:10px 20px 16px;border-top:1px solid #e2e8f0;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            <td style="vertical-align:bottom;">
              <div style="font-size:9px;color:#94a3b8;line-height:1.5;">Pacis Insurance Company Limited is regulated by the Insurance Regulatory Authority of Kenya.</div>
              <div style="font-size:9px;color:#94a3b8;line-height:1.5;">This is a computer-generated document and does not require a signature.</div>
            </td>
            <td style="text-align:right;vertical-align:bottom;white-space:nowrap;">
              <div style="font-size:9px;font-weight:700;color:#1E4276;">Quote — ${new Date().toLocaleDateString('en-KE')}</div>
            </td>
          </tr>
        </table>
      </td>
    </tr>

  </table>
  </td></tr>
</table>
</body>
</html>`;

        await axios.post('/customer/digital-notification/client-api/v1/emails', {
          to: this.customerEmail,
          subject: `Motor Insurance Invoice — ${vehicleStr} | Pacis Insurance`,
          body: emailBody,
        }, { _skipErrorRedirect: true });

        this.$notify.success(`Quote emailed to ${this.customerEmail}`, 'Sent!');
        this.emailDialogOpen = false;
        this.customerName = '';
        this.customerEmail = '';
      } catch (err) {
        this.$notify.fromError(err, 'Email Failed');
      } finally {
        this.sendingEmail = false;
      }
    },

    async confirmPdfDownload() {
      if (!this.quoteResponse) return;
      this.generatingPdf = true;
      try {
        // eslint-disable-next-line no-undef
        const { jsPDF: JsPDF } = window.jspdf;
        const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
        await this.buildQuotePDF(doc, {
          displayName: this.pdfClientName,
          displayEmail: this.pdfClientEmail,
        });
        const blob = doc.output('bloburl');
        window.open(blob, '_blank');
        this.pdfDialogOpen = false;
        this.pdfClientName = '';
        this.pdfClientEmail = '';
      } catch (err) {
        this.$notify.error('Could not generate PDF');
      } finally {
        this.generatingPdf = false;
      }
    },

    async printPDF() {
      // Legacy direct call path — still works (e.g. from keyboard shortcut)
      await this.confirmPdfDownload();
    },
    saveQuote() {
      trackAgentInteraction(this, 'underwriting_save_quote', {
        quote_id: this.quoteResponse?.quoteId || 'unknown',
      });
      // Quotes auto-save immediately to DB
      this.$notify.success('Quote securely saved to pipeline!');
    },
  },
};
</script>

<style scoped>
/* ── Body Padding ── */
.uw-body {
  padding: 20px 32px;
}

/* ── Single-Window Layout ── */
.quote-container {
  max-width: 1100px;
  margin: 0 auto;
}

.quote-layout {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 20px;
  align-items: start;
}

.quote-form-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Section Cards ── */
.section-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  padding: 18px 20px;
  border: 1px solid #E8EDF2;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.section-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #00AEEF;
  color: white;
  font-weight: 700;
  font-size: 0.75rem;
  margin-right: 8px;
  flex-shrink: 0;
}

.section-header h3 {
  margin: 0;
  font-size: 1rem;
  color: #1E4276;
  font-weight: 700;
}

/* ── Radio Pills ── */
.radio-pill-group {
  display: flex;
  gap: 8px;
}

.radio-pill {
  position: relative;
  cursor: pointer;
  flex: 1;
}

.radio-pill input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.radio-pill-content {
  border: 1.5px solid #E8EDF2;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: white;
  transition: all 0.2s;
  height: 100%;
}

.radio-pill input:checked + .radio-pill-content {
  border-color: #00AEEF;
  background: rgba(0,174,239,0.04);
  box-shadow: 0 0 0 1px #00AEEF;
}

.radio-pill input:checked + .radio-pill-content::after {
  content: "✓";
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background-color: #00AEEF;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 11px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.radio-pill-content svg {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.radio-pill-content span {
  font-weight: 600;
  color: #1E4276;
  font-size: 0.82rem;
  line-height: 1.2;
}

/* ── Paired Section ── */
.paired-section {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 16px;
}

.sub-section .sub-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 8px;
  display: block;
}

/* ── Form Row ── */
.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 0;
}

.form-row .input-group {
  flex: 1;
  margin-bottom: 0;
}

.input-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1E4276;
  margin-bottom: 6px;
  display: block;
}

/* ── Vuetify Input Overrides ── */
::v-deep .custom-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  background: #ffffff !important;
  border-radius: 8px !important;
  border: 1px solid #E2E8F0 !important;
  min-height: 44px !important;
  box-shadow: none !important;
}

::v-deep .custom-input.v-text-field--outlined fieldset { border: none !important; }

::v-deep .custom-input.v-text-field--outlined.v-input--is-focused > .v-input__control > .v-input__slot {
  border-color: #00AEEF !important;
  box-shadow: 0 0 0 3px rgba(0,174,239,0.15) !important;
}

::v-deep .custom-input .v-input__append-inner { margin-top: 10px !important; }
::v-deep .custom-input .v-input__append-inner .v-icon { font-size: 20px !important; color: #94A3B8 !important; }
::v-deep .custom-input input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1E4276 !important; padding: 9px 12px !important; }
::v-deep .custom-input input::placeholder { color: #94A3B8 !important; opacity: 1 !important; }

/* ── Amendment Banner ── */
.amendment-banner {
  display: flex;
  align-items: center;
  background: rgba(255,193,7,0.12);
  border: 1px solid rgba(255,193,7,0.3);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #FFC107;
  margin-bottom: 12px;
}

/* ══════════════════════════════════════════════════════════════════
   ADDITIONAL BENEFITS — Benefit Toggle Cards
══════════════════════════════════════════════════════════════════ */
.benefit-card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.benefit-toggle-card {
  position: relative;
  cursor: pointer;
  display: block;
}

.benefit-toggle-card input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.btc-inner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 12px;
  border: 1.5px solid #E8EDF2;
  border-radius: 8px;
  background: white;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
  min-height: 56px;
}

.benefit-toggle-card.is-checked .btc-inner {
  border-color: #00AEEF;
  background: rgba(0,174,239,0.04);
  box-shadow: 0 0 0 1px #00AEEF;
}

.benefit-toggle-card.is-updating {
  opacity: 0.65;
  pointer-events: none;
}

/* ══════════════════════════════════════════════════════════════════
   THIRD PARTY ONLY — Benefit List
══════════════════════════════════════════════════════════════════ */
.tpo-benefit-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #E8EDF2;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 12px;
}

.tpo-benefit-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid #F1F5F9;
  background: white;
  transition: background 0.15s;
}

.tpo-benefit-row:last-child { border-bottom: none; }
.tpo-benefit-row:hover { background: #F8FBFF; }

.tpo-sub-row {
  padding-top: 6px;
  padding-bottom: 10px;
  border-top: none;
}

.tpo-sub-row .tpo-icon-wrap { width: 34px; height: 34px; }

.tpo-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tpo-row-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.tpo-row-title { font-size: 0.82rem; font-weight: 700; color: #1E4276; }
.tpo-row-sub { font-size: 0.73rem; color: #64748b; }
.tpo-row-amount { flex-shrink: 0; }

.tpo-limit-badge {
  display: inline-block;
  background: rgba(0,174,239,0.1);
  color: #0077aa;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
}

.tpo-badge-green { background: rgba(56,161,105,0.1); color: #276749; }
.tpo-badge-red { background: rgba(229,62,62,0.1); color: #C53030; }

.tpo-flat-note {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.73rem;
  color: #92400e;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 7px;
  padding: 8px 12px;
  margin-top: 4px;
}

/* ── Sticky Sidebar ── */
.quote-sidebar { position: sticky; top: 16px; }

.quote-live-card {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  color: white;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(30,66,118,0.22);
}

.card-title-label {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.55);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
}

.vehicle-name { font-size: 1.05rem; font-weight: 800; margin-bottom: 2px; color: white; }
.vehicle-value-label { font-size: 0.78rem; color: rgba(255,255,255,0.5); margin-bottom: 6px; }
.vehicle-rate-label { font-size: 0.75rem; color: rgba(255,255,255,0.75); margin: 0 0 14px; font-weight: 600; }
.live-recalc-hint {
  display: flex; align-items: center; font-size: 0.72rem; color: rgba(255,255,255,0.7);
  margin-bottom: 10px;
}
.live-breakdown--dimmed { opacity: 0.55; pointer-events: none; }

.quote-placeholder { text-align: center; padding: 20px 12px; opacity: 0.55; }
.quote-placeholder p { font-size: 0.82rem; line-height: 1.4; margin-top: 8px; color: white; }

.live-breakdown { border-top: 1px solid rgba(255,255,255,0.12); padding-top: 12px; }

.live-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 7px;
  font-size: 0.82rem;
  color: white;
}

.live-row.dim { opacity: 0.5; font-size: 0.78rem; }

.live-row.total {
  font-size: 1.2rem;
  font-weight: 800;
  color: #ffd700;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,0.12);
  margin-bottom: 0;
}

.live-row.total span { color: #ffd700; }

.sidebar-actions { display: flex; gap: 6px; margin-top: 10px; }

.sidebar-action-btn {
  flex: 1;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  padding: 6px 4px;
  color: white;
  font-size: 0.68rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.sidebar-action-btn:hover { background: rgba(255,255,255,0.18); }

.cta-bar { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }

.btn-generate-quote {
  background: #FFC107;
  color: #1E4276;
  border: none;
  padding: 11px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 3px 10px rgba(255,209,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-generate-quote:hover:not(:disabled) { filter: brightness(0.95); transform: translateY(-1px); }
.btn-generate-quote:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-generate-quote.generated { background: #10B981 !important; color: white !important; }

.btn-convert-sale {
  background: #00AEEF;
  color: white;
  border: none;
  padding: 12px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.95rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0,174,239,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-convert-sale:hover { opacity: 0.9; }

/* ── Mobile Bottom Bar ── */
.mobile-bottom-bar {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  color: white;
  flex-direction: column;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.2);
}

.mobile-bar-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  min-height: 56px;
}

.bar-left { display: flex; flex-direction: column; }
.bar-label { font-size: 0.65rem; color: rgba(255,255,255,0.55); text-transform: uppercase; letter-spacing: 0.4px; }
.bar-total { font-size: 1.2rem; font-weight: 800; color: #ffd700; }
.bar-actions { display: flex; gap: 8px; align-items: center; }

.expand-btn {
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  padding: 6px 10px;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.bar-cta {
  background: #FFC107;
  color: #1E4276;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 700;
  font-size: 0.82rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.bar-cta:disabled { opacity: 0.5; cursor: not-allowed; }

.mobile-bar-drawer {
  display: none;
  padding: 0 16px 14px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.mobile-bar-drawer.open { display: block; }
.mobile-bar-drawer .live-row { font-size: 0.8rem; margin-bottom: 5px; }
.mobile-bar-drawer .live-row.dim { font-size: 0.75rem; }
.mobile-bar-drawer .live-row.total { font-size: 1.1rem; }

.drawer-actions {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255,255,255,0.1);
}

.drawer-action-btn {
  flex: 1;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  padding: 6px;
  color: white;
  font-size: 0.68rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: pointer;
}

/* ── Responsive: Tablet ── */
@media (max-width: 960px) {
  .quote-layout { grid-template-columns: 1fr; padding-bottom: 80px; }
  .paired-section { grid-template-columns: 1fr 1fr; gap: 12px; }
  .quote-sidebar { display: none; }
  .mobile-bottom-bar { display: flex !important; }
}

/* ── Responsive: Mobile ── */
@media (max-width: 600px) {
  .uw-body { padding: 12px 12px 90px 12px; }
  .section-card { padding: 14px 14px; }
  .section-header { margin-bottom: 10px; }
  .section-header h3 { font-size: 0.92rem; }
  .section-number { width: 22px; height: 22px; font-size: 0.7rem; }
  .paired-section { grid-template-columns: 1fr 1fr; gap: 10px; }
  .sub-section .sub-label { font-size: 0.7rem; margin-bottom: 6px; }
  .radio-pill-content { padding: 8px 8px; gap: 7px; border-radius: 6px; }
  .radio-pill-content svg { width: 24px; height: 24px; }
  .radio-pill-content span { font-size: 0.72rem; }
  .radio-pill-group { gap: 6px; }
  .radio-pill input:checked + .radio-pill-content::after { width: 16px; height: 16px; font-size: 9px; top: -6px; right: -6px; }
  .form-row { flex-direction: column; gap: 10px; }
  .input-label { font-size: 0.75rem; }
  .benefit-card-grid { grid-template-columns: 1fr; }
  .quote-form-column { gap: 12px; }
}
</style>

<!-- Unscoped: push third-party chat widgets above the mobile bottom bar -->
<style>
/* ── Push chat widgets above mobile bar ── */
@media (max-width: 960px) {
  #tawk-bubble-container, .tawk-min-container,
  iframe[title*="chat" i], iframe[title*="tawk" i],
  .intercom-lightweight-app, .crisp-client, #drift-widget, #launcher,
  [class*="chat-widget"], [id*="chat-widget"],
  div[style*="position: fixed"][style*="bottom: 0"],
  div[style*="position: fixed"][style*="bottom:0"] {
    bottom: 72px !important;
    transform: scale(0.8);
    transform-origin: bottom right;
  }
}

/* ── Email Quote Dialog Custom Styles ── */
.eq-dialog {
  background: white !important;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
}

.eq-header {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  padding: 20px 20px 18px;
  display: flex !important;
  align-items: center;
  gap: 14px;
  border-bottom: none;
  position: relative;
}

.eq-header-icon {
  width: 46px;
  height: 46px;
  background: #00AEEF;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.3);
}

.eq-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: white;
  margin: 0;
}

.eq-subtitle {
  font-size: 0.78rem;
  color: rgba(255,255,255,0.65);
  margin: 0;
  line-height: 1.2;
}

.eq-close {
  position: absolute;
  top: 14px;
  right: 14px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  cursor: pointer;
  padding: 4px;
  transition: background 0.2s;
}

.eq-close:hover { background: rgba(255,255,255,0.2); }

.eq-body {
  padding: 24px 20px;
}

.eq-field {
  margin-bottom: 20px;
}

.eq-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1E4276;
  margin-bottom: 6px;
}

.eq-req { color: #ef4444; }

.eq-input-wrap {
  position: relative;
  display: flex;
  align-items: stretch;
}

.eq-input {
  width: 100%;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 12px 11px 40px;
  font-size: 0.875rem;
  color: #1e293b;
  transition: all 0.2s;
  line-height: 1.5;
  min-height: 44px;
}

.eq-input:focus {
  outline: none;
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.1);
}

.eq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
}

.eq-preview-strip {
  display: flex !important;
  align-items: flex-start;
  gap: 8px;
  background: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 0.75rem;
  color: #0369a1;
  font-weight: 500;
  margin-top: 10px;
  line-height: 1.5;
}

.eq-footer {
  padding: 16px 20px;
  background: #f8fafc;
  display: flex !important;
  justify-content: space-between;
  gap: 12px;
}

.eq-btn-cancel {
  flex: 1;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
  color: #64748b;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
}

.eq-btn-send {
  flex: 1.5;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #00AEEF;
  color: #1E4276;
  font-weight: 800;
  font-size: 0.88rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 174, 239, 0.25);
}

.eq-btn-send:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(0, 174, 239, 0.35); }
.eq-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }

@media screen {
  .print-container { display: none !important; }
}

@media print {
  @page {
    margin: 0 !important;
    size: A4 portrait;
  }

  /* Hide everything via visibility (not display) so #app isn't nuked */
  body * { visibility: hidden !important; }

  .print-container,
  .print-container * { visibility: visible !important; }

  /*
   * KEY FIX: position:fixed (NOT absolute or static).
   * Fixed positioning with width: 100% ensures the template stretches
   * to fit the paper dimensions, while inset padding handles margins.
   */
  .print-container {
    display: block !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: auto !important;
    padding: 10mm 12mm !important;
    background: white !important;
    font-family: 'Helvetica Neue', Arial, sans-serif;
    font-size: 11.5px;
    color: #1e293b;
    box-sizing: border-box;
    transform: none !important;
    z-index: 9999 !important;
  }

  .print-container * {
    box-sizing: border-box;
  }

  /* Force body/html to reset during print to avoid centering offsets */
  html, body {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block !important;
    overflow: visible !important;
    background: white !important;
  }

  /* ── Letterhead ── */
  .pq-letterhead {
    display: flex !important;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1.5px solid #1E4276;
    width: 100%;
    box-sizing: border-box;
  }
  .pq-logo { height: 38px; object-fit: contain; }
  .pq-logo-wrap { display: flex; align-items: center; }
  .pq-letterhead-right {
    text-align: right;
    max-width: 55%;
    word-break: break-word;
  }
  .pq-insurer-name { font-size: 12px; font-weight: 700; color: #1E4276; margin: 0 0 1px; }
  .pq-insurer-addr  { font-size: 10px; color: #64748b; margin: 0; line-height: 1.4; }

  /* ── Title band ── */
  .pq-title-band {
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    background: #1E4276 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color: white !important;
    padding: 8px 12px;
    border-radius: 5px;
    margin-bottom: 12px;
  }
  .pq-doc-type { font-size: 12px; font-weight: 800; letter-spacing: 0.4px; }
  .pq-doc-date { font-size: 10px; opacity: 0.8; }

  /* ── Two-column info grid ── */
  .pq-info-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 12px;
  }
  .pq-info-block { border: 1px solid #e2e8f0; border-radius: 5px; overflow: hidden; }
  .pq-info-heading {
    font-size: 9px; font-weight: 800; color: #1E4276 !important;
    text-transform: uppercase; letter-spacing: 0.5px;
    background: #EBF5FF !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    padding: 4px 10px; margin: 0; border-bottom: 1px solid #e2e8f0;
  }
  .pq-info-table { width: 100%; border-collapse: collapse; }
  .pq-info-table tr { border-bottom: 1px solid #f1f5f9; }
  .pq-info-table tr:last-child { border-bottom: none; }
  .pq-info-key { font-size: 10px; font-weight: 600; color: #64748b; padding: 4px 10px; width: 42%; }
  .pq-info-val { font-size: 11px; font-weight: 700; color: #1E4276; padding: 4px 10px; }

  /* ── Section heading ── */
  .pq-section-heading {
    font-size: 9px; font-weight: 800; color: #1E4276;
    text-transform: uppercase; letter-spacing: 0.5px;
    border-bottom: 2px solid #1E4276;
    padding-bottom: 3px; margin: 0 0 7px;
  }

  /* ── Premium table ── */
  .pq-premium-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  .pq-premium-table thead tr {
    background: #EBF5FF !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .pq-premium-table th {
    font-size: 9px; font-weight: 700; color: #1E4276;
    text-transform: uppercase; letter-spacing: 0.4px;
    padding: 5px 10px; text-align: left;
    border-bottom: 2px solid #1E4276;
  }
  .pq-col-amt { text-align: right !important; }
  .pq-premium-table td { padding: 5px 10px; font-size: 11px; border-bottom: 1px solid #f1f5f9; }
  .pq-subtotal-row td { color: #64748b; font-size: 10px; }
  .pq-total-row td {
    padding: 7px 10px; font-size: 13px; font-weight: 800;
    color: #1E4276 !important;
    border-top: 2px solid #1E4276;
    background: #EBF5FF !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }

  /* ── Validity strip ── */
  .pq-validity-strip {
    display: flex !important; align-items: center; gap: 5px;
    background: #FFFBEB !important;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
    border: 1px solid #FDE68A; border-radius: 5px;
    padding: 5px 10px; font-size: 10px; color: #92400E; margin-bottom: 12px;
  }

  /* ── Footer ── */
  .pq-footer {
    display: flex !important; justify-content: space-between; align-items: flex-end;
    padding-top: 8px; border-top: 1px solid #e2e8f0;
  }
  .pq-footer-line { font-size: 9px; color: #94a3b8; margin: 0 0 1px; line-height: 1.4; }
  .pq-footer-right { text-align: right; flex-shrink: 0; }
}
.uw-new-btn {
  display: flex; align-items: center;
  height: 32px; padding: 0 12px;
  background: white; border: 1.5px solid #E8EDF2;
  border-radius: 8px; font-size: .75rem; font-weight: 700;
  color: #1E4276; cursor: pointer; transition: all .15s;
}
.uw-new-btn:hover { border-color: #00AEEF; color: #00AEEF; }

/* Shared dialog styles (same as AgentSale) */
.new-sale-dialog { background: white; border-radius: 12px; overflow: hidden; }
.nsd-header { display: flex; align-items: center; gap: 12px; padding: 18px 20px 14px; background: linear-gradient(135deg, #1E4276, #2A5A9E); }
.nsd-icon { width: 40px; height: 40px; background: rgba(239,68,68,.25); border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.nsd-title { font-size: .92rem; font-weight: 800; color: white; margin: 0 0 2px; }
.nsd-sub { font-size: .72rem; color: rgba(255,255,255,.65); margin: 0; }
.nsd-body { padding: 16px 20px; }
.nsd-desc { font-size: .82rem; color: #64748b; margin: 0; line-height: 1.5; }
.nsd-footer { display: flex; gap: 10px; padding: 12px 20px 16px; background: #F8FAFC; border-top: 1px solid #F1F5F9; }
.nsd-btn-cancel { flex: 1; padding: 10px; border: 1.5px solid #E8EDF2; border-radius: 8px; background: white; color: #64748b; font-weight: 600; font-size: .84rem; cursor: pointer; }
.nsd-btn-confirm { flex: 1; padding: 10px; border: none; border-radius: 8px; background: #EF4444; color: white; font-weight: 700; font-size: .84rem; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.nsd-btn-confirm:hover { background: #DC2626; }
</style>
